// components/DevStudio.jsx
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNotification } from '../components/NotificationSystem';
import { 
  buildRandomSceneDescription,
  stripLabel,
  COLOR_LIBRARY,
  TEXTURE_MATERIAL_LIBRARY,
  TECHNIQUE_LIBRARY,
  CUSTOM_COLORS,
  CUSTOM_MATERIALS
} from '../libraries/promptObjectLibrary';
import { 
  FiArrowLeft, FiImage, FiX, FiDownload, FiTrash2, FiRefreshCcw, FiUsers, FiChevronDown, FiLayers, FiGrid, FiCheckCircle, FiCopy, FiLink, FiChevronRight, FiInfo, FiZap, FiPlus, FiCamera
} from 'react-icons/fi';
import { FaCog } from 'react-icons/fa';
import { MdKeyboardArrowRight } from 'react-icons/md';
import { RiImageCircleAiLine } from 'react-icons/ri';
import { IoWarningOutline, IoInformationCircleOutline } from 'react-icons/io5';
import { saveAs } from 'file-saver';
// JSZip will be dynamically imported for bulk downloads
import axios from 'axios';
import {
  doc, updateDoc, serverTimestamp, onSnapshot,
  collection, addDoc, query, where, orderBy, getDocs, deleteDoc, getDoc
} from 'firebase/firestore';
import { getStorage, ref, uploadString, getDownloadURL, deleteObject } from 'firebase/storage';
import { db } from '../firebase';
import Replicate from 'replicate';
import { 
  checkSufficientCredits, 
  deductImageCredits,
  getRemainingCredits,
  safeGenerate
} from '../services/usageService';



// ============================================
// MODEL CONFIGURATIONS
// ============================================
const models = [
  {
    id: 'recraft',
    name: 'Recraft V3',
    endpoint: 'recraft-v3',
    description: 'High-quality vector & realistic renders',
    defaultSize: '1024x1024',
    outputFormat: 'png',
    pricing: 0.05,
  },
  {
    id: 'nanobanana',
    name: 'Nano Banana Pro',
    endpoint: 'nano-banana-pro',
    description: 'Google model with fast generation & multi-image support',
    defaultAspectRatio: '9:16',
    defaultSize: '4K',
    outputFormat: 'jpg',
    pricing: { '1K': 0.15, '2K': 0.15, '4K': 0.25 },
  },
  {
    id: 'seedream',
    name: 'Seedream 5.0 Lite',
    endpoint: 'seedream-v5-lite-text-to-image',
    description: 'Fast multimodal text-to-image, instruction-following',
    defaultAspectRatio: '9:16',
    defaultSize: '2K',
    outputFormat: 'jpg',
    pricing: 0.035,
  },
  {
    id: 'kling',
    name: 'Kling V3',
    endpoint: 'kling-3-text2image',
    description: 'Photorealistic, print-ready images with 2K output',
    defaultAspectRatio: '9:16',
    defaultSize: '1K',
    outputFormat: 'png',
    pricing: 0.035,
  },
];

// ============================================
// CAMERA ANGLES  (Loose Cropped Framing, Distant Flat‑Field, No Keystone Compression)
// ============================================
const cameraAngles = [
  {
    id: 'chest-high-downward',
    label: 'Handheld Wide (Candid Cropped)',
    description: '18‑20mm rectilinear, distant 8‑12ft, loose framing, zero edge stretch, sofa faces camera',
    instruction: `A vertical 9:16 smartphone photo taken with a rectilinear ultra‑wide lens (18‑20mm equivalent). The perspective is a Flat‑Field rectilinear projection: all straight lines remain perfectly straight across the entire frame. The subject distance is at least 8‑12 feet — the camera is placed far back, eliminating any foreground bulging or edge stretching. A natural handheld downward tilt of 8‑10 degrees is allowed, but the vertical heights of furniture must remain proportional; no keystone compression that makes sofas appear vertically stretched. The framing is loose and candid — some furniture may be partially cropped or cut off at the edges. Do not force every item into the frame. The main sofa faces directly toward the camera (front‑facing composition), and the sofa is the clear focal point, slightly off‑center. No black borders, no vignette, no lens hardware. Image is naturally bright. No devices, no UI.`,
  },
  {
    id: 'establishing-far',
    label: 'Establishing / Very Far (Loose Crop)',
    description: '18mm rectilinear, distant 12ft+, zero stretch, loose framing, sofa faces camera',
    instruction: `An establishing shot with a rectilinear 18mm lens, camera far back (12+ feet). Flat‑field rectilinear projection — no edge stretching, no bulging. Framing is intentionally loose; the entire room does not need to be fully contained — objects at the edges may be cut off. The main sofa faces the camera and is noticeable. Composition is asymmetrical and natural. Bright, evenly lit. No black borders, no lens hardware. No devices, no UI.`,
  },
  {
    id: 'left-corner-anchor',
    label: 'Left Corner Anchor (Cropped Diagonal)',
    description: '24mm rectilinear, distant low left corner, diagonal, zero stretch, loose crop, sofa faces camera',
    instruction: `A cinematic 24mm rectilinear shot, camera placed low in the left corner at a distance of at least 8‑10 feet, looking diagonally across the room. Flat‑field rectilinear projection — verticals are straight, no barrel distortion, no edge stretching. The left corner object may be heavily cropped or only partially visible as an anchor. The main sofa faces the camera directly, occupying about 40‑50% of the frame, without foreground exaggeration. Furniture on the far right can be cut off by the frame edge. No forced inclusion of every piece. Bright, dynamic. No black borders, no lens hardware. No devices, no UI.`,
  },
  {
    id: 'right-corner-anchor',
    label: 'Right Corner Anchor (Cropped Diagonal)',
    description: '24mm rectilinear, distant low right corner, diagonal, zero stretch, loose crop, sofa faces camera',
    instruction: `A cinematic 24mm rectilinear shot, camera low in the right corner at a distance of at least 8‑10 feet, looking diagonally toward the opposite corner. Flat‑field rectilinear projection — straight verticals, no edge stretching. The right corner object may be partially cropped. The main sofa faces the camera, slightly off‑center, roughly 40‑50% of the frame, comfortable distance. Furniture on the left may be cut off. Asymmetrical balance — no requirement to show everything. Bright, balanced lighting. No black borders, no lens hardware. No devices, no UI.`,
  },
  {
    id: 'straight-on-frontal',
    label: 'Straight‑On / Frontal (Cropped Editorial)',
    description: '35mm rectilinear, distant eye‑level, zero stretch, loose crop, sofa faces camera',
    instruction: `A 35mm rectilinear lens at standing eye level, camera positioned at least 8‑10 feet away, facing the room directly. Flat‑field rectilinear projection — verticals are upright, no barrel distortion. Framing is loose and editorial: items at the edges may be partially cropped; the composition feels candid, not forced. The main sofa faces the camera directly, slightly off‑center, with natural proportions and no vertical compression. Bright, naturally lit. No black borders, no lens hardware. No devices, no UI.`,
  },
  {
    id: 'asymmetrical-distant-corners',
    label: 'Asymmetrical Distant (Cropped Corners)',
    description: '24mm rectilinear, distant corner, far corner anchor, sofa faces camera, loose crop, zero stretch',
    instruction: `An asymmetrical 24mm rectilinear composition, camera placed in one corner at a distance of 8‑12 feet, elevated slightly above eye level, looking diagonally across the room. Flat‑field rectilinear projection — straight verticals, zero edge stretching. The far opposite corner serves as an anchor; the near corner object is heavily cropped. The main sofa faces the camera, slightly off‑center, filling 40‑50% of the frame with no exaggerated foreground. The framing is deliberately loose — objects can be cut off at the edges. Bright, with intentional negative space. No black borders, no lens hardware. No devices, no UI.`,
  },
  {
    id: 'close-up-50mm',
    label: 'Close‑Up / Intimate (50mm)',
    description: '50mm, shallow depth of field, on texture or object, bright',
    instruction: `A 50mm close‑up shot, camera close to a specific element (sofa fabric, coffee table surface, plant, etc.). Shallow depth of field blurs the background. The subject’s texture and materiality are rendered with intimate detail. The image is bright. No black borders, no lens hardware. No devices, no UI.`,
  },
];

// ============================================
// SHARED POSITIVE PROMPT FOR SMARTPHONE REALISM
// ============================================
const SHARED_POSITIVE_PROMPT = `True 18mm smartphone ultrawide, unedited raw photo, strict rectilinear projection. Camera at human chest height (1.6m above floor), placed flat against the opposite wall looking straight across the room, natural 5-10° downward tilt. Subject distance 5.5 meters from the main sofa. The primary sofa must occupy less than 30% of the vertical frame height and sit in the mid-to-background, not the foreground. Full-frame coverage with evenly lit corners, no black vignette. Ceilings are completely flat, smooth, uniform, and featureless – no texture, no distressing, no bumps, no weathering. Unedited raw smartphone photo, matte textile fabric, visible fabric weave and micro-fibers, slightly uneven sagging tufting caused by actual physical weight, natural creases, raw unpolished wood grain with knots and pores, slight warping and canvas weave texture on the blank wall art, physical thick art frame, non-repeating irregular wooden floorboard patterns, low contrast, slightly desaturated colors, high ISO sensor noise, natural daylight washout, imperfect physical seams, zero 3D render traits. Natural edge stretching, asymmetrical furniture placement, loose framing, partial cropping allowed.`;

// ============================================
// SHARED NEGATIVE PROMPT (APPLIED HEAVILY TO KLING)
// ============================================
const SHARED_NEGATIVE_PROMPT = `Glossy surface, wet look, polished plastic, inflated balloon, 3D CGI render, perfectly symmetrical round tufts, over-saturated colors, perfect geometric symmetry, hyper-real, high contrast, studio lighting, perfectly clean fabric, digital illustration, flat glowing white rectangle, perfectly uniform floor tile pattern, mathematical repeating grain, vector art, flawless geometry, smooth wax finish, heavy vignette, black borders, dark corners, dollhouse miniature, flat vector texture, painted lines, plastic toy, perfectly smooth plastic wood, polished resin, glassy wood finish, corner angle shot, sofa in extreme foreground, heroic furniture close-up, distressed ceiling, textured ceiling, ceiling scratches, ceiling bumps, weathered ceiling, popcorn ceiling`;

// ============================================
// KLING-SPECIFIC PROMPT SUFFIX (TEXTURE GRAIN INJECTION)
// ============================================
const KLING_TEXTURE_GRAIN_SUFFIX = `, raw sensor grain, slight digital noise, unedited photographic texture, no AI sharpening.`;

// ============================================
// TEXTURE PLACEHOLDER BADGE COMPONENT
// ============================================
const TexturePlaceholderBadge = ({ textureName, onRemove, compact = false }) => (
  <span
    contentEditable={false}
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: '4px',
      background: 'linear-gradient(135deg, #88b7ff 0%, #6ba6ff 100%)',
      color: 'white',
      padding: compact ? '2px 8px' : '4px 12px',
      borderRadius: '20px',
      fontSize: compact ? '0.7rem' : '0.8rem',
      fontWeight: 600,
      fontFamily: 'Avenir, sans-serif',
      margin: '0 2px',
      cursor: 'default',
      userSelect: 'none',
      boxShadow: '0 2px 8px rgba(136, 183, 255, 0.3)',
      whiteSpace: 'nowrap',
      verticalAlign: 'middle',
      border: '1px solid rgba(255,255,255,0.2)',
    }}
  >
    <FiImage size={12} style={{ flexShrink: 0 }} />
    {textureName}
    {onRemove && (
      <span
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onRemove();
        }}
        style={{
          cursor: 'pointer',
          marginLeft: '2px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '16px',
          height: '16px',
          borderRadius: '50%',
          background: 'rgba(255,255,255,0.2)',
          fontSize: '10px',
          transition: 'background 0.2s',
          flexShrink: 0,
        }}
        onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.4)'}
        onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.2)'}
      >
        ×
      </span>
    )}
  </span>
);

// ============================================
// GALLERY ITEM COMPONENT
// ============================================
const GalleryItem = ({ image, onSelect, onDownload, onDelete, onReuse, onToggleSelect, isSelected, showCheckboxes, onUpscale }) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  
  return (
    <div
      className="gallery-item-masonry"
      onClick={(e) => {
        // Don't trigger select if clicking on checkbox or action buttons
        if (e.target.closest('.gallery-checkbox') || e.target.closest('.gallery-action-btn')) return;
        if (image.imageUrl && !imageError && !isDeleting) {
          onSelect();
        }
      }}
      style={{ position: 'relative', opacity: imageError || isDeleting ? 0.5 : 1 }}
    >
      {/* Selection checkbox */}
      {showCheckboxes && (
        <div 
          className="gallery-checkbox"
          style={{
            position: 'absolute',
            top: '8px',
            left: '8px',
            zIndex: 10,
            width: '28px',
            height: '28px',
            borderRadius: '50%',
            background: isSelected ? '#88b7ff' : 'rgba(255,255,255,0.85)',
            border: isSelected ? '2px solid #88b7ff' : '2px solid rgba(0,0,0,0.2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
          }}
          onClick={(e) => {
            e.stopPropagation();
            onToggleSelect?.(image.id);
          }}
        >
          {isSelected ? (
            <FiCheckCircle size={16} color="white" />
          ) : (
            <span style={{ color: '#999', fontSize: '14px', fontWeight: 'bold' }}>○</span>
          )}
        </div>
      )}
      {image.imageUrl && !imageError && !isDeleting ? (
        <img
          src={image.imageUrl}
          alt={image.prompt || image.shortDescription || 'Generated image'}
          style={{ width: '100%', display: 'block' }}
          loading="lazy"
          decoding="async"
          onLoad={() => setImageLoaded(true)}
          onError={() => {
            console.error('Failed to load image:', image.id, image.imageUrl);
            setImageError(true);
            setImageLoaded(true);
            // DON'T auto-delete - just show error state
            // The image might still exist in Firestore, just the URL is broken
          }}
        />
      ) : (
        <div style={{ 
          width: '100%', 
          aspectRatio: '1/1', 
          background: '#f5f5f5',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#999',
          fontSize: '0.7rem'
        }}>
          <div style={{ textAlign: 'center' }}>
            <FiImage size={24} style={{ marginBottom: '8px', opacity: 0.3 }} />
            <div>{isDeleting ? 'Removing...' : 'Loading...'}</div>
          </div>
        </div>
      )}
      
      {imageLoaded && !imageError && !isDeleting && (
        <>
          {/* Top buttons: Regenerate + Trash */}
          {image.workflowType === 'texture-to-scene' && (
            <button
              className="gallery-action-btn"
              onClick={(e) => { e.stopPropagation(); onReuse(); }}
              style={{ position: 'absolute', top: '8px', right: '48px', background: 'rgba(136, 183, 255, 0.8)', border: 'none', borderRadius: '50%', width: '28px', height: '28px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: 'white', zIndex: 2 }}
              title="Reuse this setup"
            >
              <FiRefreshCcw size={12} />
            </button>
          )}
          <button
            className="gallery-action-btn"
            onClick={(e) => { e.stopPropagation(); onDelete(); }}
            style={{ position: 'absolute', top: '8px', right: '8px', background: 'rgba(224, 47, 29, 0.8)', border: 'none', borderRadius: '50%', width: '28px', height: '28px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: 'white', zIndex: 2 }}
          >
            <FiTrash2 size={12} />
          </button>

          {/* Bottom buttons: Scene/Texture label + Upscale (scenes only) + Download */}
          <div style={{ position: 'absolute', bottom: '68px', left: '8px', background: 'rgba(0,0,0,0.6)', borderRadius: '10px', padding: '2px 8px', fontSize: '0.6rem', color: 'white', fontFamily: 'Avenir, sans-serif', zIndex: 2 }}>
            {image.type === 'texture' ? 'Texture' : 'Scene'}
          </div>
          {onUpscale && image.type !== 'texture' && (
            <button
              className="gallery-action-btn"
              onClick={(e) => { e.stopPropagation(); onUpscale(image); }}
              style={{ position: 'absolute', bottom: '68px', right: '48px', background: 'rgba(136, 183, 255, 0.8)', border: 'none', borderRadius: '50%', width: '28px', height: '28px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: 'white', zIndex: 2 }}
              title="Upscale to 4K"
            >
              <FiZap size={12} />
            </button>
          )}
          <button
            className="gallery-action-btn"
            onClick={(e) => { e.stopPropagation(); onDownload(); }}
            style={{ position: 'absolute', bottom: '68px', right: '8px', background: 'rgba(0,0,0,0.6)', border: 'none', borderRadius: '50%', width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: 'white', zIndex: 2 }}
          >
            <FiDownload size={14} />
          </button>
        </>
      )}
      
      <div style={{ padding: '10px', borderTop: '1px solid #f0f0f0', background: 'white', position: 'relative' }}>
        <div style={{ fontFamily: 'Avenir, sans-serif', fontSize: '0.7rem', fontWeight: 600, color: '#333', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {imageError || isDeleting 
            ? '⚠️ Image missing' 
            : (image.type === 'texture' 
                ? (image.shortDescription?.substring(0, 40) || 'Texture') 
                : (image.shortSceneDescription?.substring(0, 40) || 'Scene'))}
        </div>
        <div style={{ fontFamily: 'Avenir, sans-serif', fontSize: '0.6rem', color: '#aaa', marginTop: '4px' }}>
          {image.model || 'AI'} • {image.type === 'texture' ? '1:1' : (image.aspectRatio || '9:16')}
        </div>
      </div>
    </div>
  );
};

// ============================================
// IMAGE DETAILS MODAL COMPONENT
// ============================================
const ImageDetailsModal = ({ selectedImage, onClose, onDownload, onDelete, onUseTexture, isMobile }) => {
  return (
    <div 
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.85)',
        zIndex: 2000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: isMobile ? '0' : '40px',
      }}
      onClick={onClose}
    >
      <div 
        style={{
          backgroundColor: 'white',
          borderRadius: isMobile ? '0' : '20px',
          width: '100%',
          maxWidth: isMobile ? '100%' : '900px',
          height: isMobile ? '100vh' : 'auto',
          maxHeight: isMobile ? '100vh' : '90vh',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: isMobile ? 'none' : '0 25px 60px rgba(0,0,0,0.3)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          padding: '16px 20px', 
          borderBottom: '1px solid #eaeaea',
          flexShrink: 0,
          background: 'white',
        }}>
          <h3 style={{ fontFamily: 'Avenir, sans-serif', fontWeight: 600, fontSize: '1rem', color: '#333', margin: 0 }}>
            {selectedImage.type === 'texture' ? 'Texture Details' : 'Scene Details'}
          </h3>
          <button 
            onClick={onClose}
            style={{ 
              background: 'rgba(0,0,0,0.05)', 
              border: 'none', 
              borderRadius: '50%', 
              width: '36px', 
              height: '36px', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center', 
              cursor: 'pointer',
              color: '#666',
              fontSize: '18px',
            }}
          >
            <FiX size={20} />
          </button>
        </div>

        {/* Content */}
        <div style={{ 
          flex: 1, 
          overflowY: 'auto', 
          display: 'flex', 
          flexDirection: isMobile ? 'column' : 'row',
        }}>
          {/* Image */}
          <div style={{ 
            flex: 1, 
            background: '#1a1a1a', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            minHeight: isMobile ? '40vh' : 'auto',
            position: 'relative',
          }}>
            <img
              src={selectedImage.imageUrl}
              alt={selectedImage.prompt || 'Generated image'}
              style={{ 
                width: '100%', 
                height: isMobile ? '100%' : 'auto',
                maxHeight: isMobile ? '50vh' : '75vh',
                objectFit: 'contain',
                display: 'block',
              }}
            />
            {selectedImage.type === 'texture' && onUseTexture && (
              <button
                onClick={() => { onUseTexture(); onClose(); }}
                style={{
                  position: 'absolute', 
                  top: '12px', 
                  left: '12px',
                  background: 'rgba(136,183,255,0.9)', 
                  border: 'none', 
                  borderRadius: '20px',
                  padding: '8px 16px', 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '8px',
                  cursor: 'pointer', 
                  color: 'white', 
                  fontFamily: 'Avenir, sans-serif', 
                  fontSize: '0.8rem', 
                  zIndex: 2,
                  fontWeight: 600,
                }}
              >
                <FiPlus size={16} /> Use This Texture
              </button>
            )}
          </div>

          {/* Details Panel */}
          <div style={{ 
            width: isMobile ? '100%' : '360px', 
            flexShrink: 0,
            overflowY: 'auto',
            padding: '24px',
            borderLeft: isMobile ? 'none' : '1px solid #eaeaea',
            borderTop: isMobile ? '1px solid #eaeaea' : 'none',
          }}>
            {selectedImage.type === 'texture' ? (
              <div style={{ marginBottom: '20px' }}>
                <div style={{ fontSize: '0.7rem', color: '#88b7ff', textTransform: 'uppercase', letterSpacing: '1px', fontFamily: 'Avenir, sans-serif', fontWeight: 600, marginBottom: '6px' }}>
                  Short Description
                </div>
                <p style={{ fontFamily: 'Avenir, sans-serif', fontSize: '0.9rem', color: '#333', lineHeight: 1.5, margin: 0 }}>
                  {selectedImage.shortDescription || 'No description'}
                </p>
              </div>
            ) : (
              <div style={{ marginBottom: '20px' }}>
                <div style={{ fontSize: '0.7rem', color: '#88b7ff', textTransform: 'uppercase', letterSpacing: '1px', fontFamily: 'Avenir, sans-serif', fontWeight: 600, marginBottom: '6px' }}>
                  Scene Description
                </div>
                <p style={{ fontFamily: 'Avenir, sans-serif', fontSize: '0.9rem', color: '#333', lineHeight: 1.5, margin: 0 }}>
                  {selectedImage.shortSceneDescription || 'No description'}
                </p>
                {selectedImage.textureShortDescription && (
                  <>
                    <div style={{ fontSize: '0.7rem', color: '#88b7ff', textTransform: 'uppercase', letterSpacing: '1px', fontFamily: 'Avenir, sans-serif', fontWeight: 600, marginTop: '16px', marginBottom: '6px' }}>
                      Applied Texture
                    </div>
                    <p style={{ fontFamily: 'Avenir, sans-serif', fontSize: '0.85rem', color: '#666', lineHeight: 1.5, margin: 0, padding: '10px', background: '#f0f4ff', borderRadius: '8px' }}>
                      {selectedImage.textureShortDescription}
                    </p>
                  </>
                )}
              </div>
            )}

            {/* Full Prompt (collapsible) */}
            {selectedImage.prompt && (
              <div style={{ marginBottom: '20px' }}>
                <div style={{ fontSize: '0.7rem', color: '#88b7ff', textTransform: 'uppercase', letterSpacing: '1px', fontFamily: 'Avenir, sans-serif', fontWeight: 600, marginBottom: '6px' }}>
                  Full Prompt
                </div>
                <div style={{ 
                  fontFamily: 'Roboto Mono, sans-serif', 
                  fontSize: '0.7rem', 
                  color: '#666', 
                  lineHeight: 1.5, 
                  background: '#f5f5f5', 
                  padding: '12px', 
                  borderRadius: '8px', 
                  maxHeight: '200px', 
                  overflowY: 'auto',
                  wordBreak: 'break-word',
                }}>
                  {selectedImage.prompt}
                </div>
              </div>
            )}

            {/* Meta Tags */}
            <div style={{ marginBottom: '20px', display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              <div style={{ padding: '4px 12px', background: '#f5f5f5', borderRadius: '20px', fontSize: '0.7rem', fontFamily: 'Avenir, sans-serif', color: '#666' }}>
                {selectedImage.type === 'texture' ? '1:1' : (selectedImage.aspectRatio || '9:16')}
              </div>
              <div style={{ padding: '4px 12px', background: '#f5f5f5', borderRadius: '20px', fontSize: '0.7rem', fontFamily: 'Avenir, sans-serif', color: '#666' }}>
                {selectedImage.resolution || '4K'}
              </div>
              <div style={{ padding: '4px 12px', background: '#f5f5f5', borderRadius: '20px', fontSize: '0.7rem', fontFamily: 'Avenir, sans-serif', color: '#666' }}>
                {selectedImage.model || 'AI'}
              </div>
              <div style={{ padding: '4px 12px', background: '#f5f5f5', borderRadius: '20px', fontSize: '0.7rem', fontFamily: 'Avenir, sans-serif', color: '#666' }}>
                {selectedImage.createdAt?.toDate ? new Date(selectedImage.createdAt.toDate()).toLocaleDateString() : 'Just now'}
              </div>
            </div>

            {/* Action Buttons */}
            <div style={{ display: 'flex', gap: '10px' }}>
              <button 
                onClick={onDownload}
                style={{ 
                  flex: 1, 
                  padding: '12px', 
                  background: '#88b7ff', 
                  border: 'none', 
                  borderRadius: '30px', 
                  color: 'white', 
                  fontFamily: 'Avenir, sans-serif', 
                  fontSize: '0.9rem', 
                  cursor: 'pointer', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center', 
                  gap: '8px',
                  fontWeight: 600,
                }}
              >
                <FiDownload size={16} /> Download
              </button>
              <button 
                onClick={() => { onDelete(); onClose(); }}
                style={{ 
                  flex: 1, 
                  padding: '12px', 
                  background: 'transparent', 
                  border: '1px solid #e02f1d', 
                  borderRadius: '30px', 
                  color: '#e02f1d', 
                  fontFamily: 'Avenir, sans-serif', 
                  fontSize: '0.9rem', 
                  cursor: 'pointer',
                  fontWeight: 600,
                }}
              >
                <FiTrash2 size={16} /> Delete
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============================================
// SCENE CHARACTER COUNT INDICATOR
// ============================================
const SceneCharCount = ({ count }) => (
  <span style={{
    fontSize: '0.7rem',
    color: count > 750 ? '#e02f1d' : '#999',
    fontFamily: 'Avenir, sans-serif',
    fontWeight: 500,
    marginLeft: '8px',
    whiteSpace: 'nowrap',
  }}>
    {count}/750
  </span>
);

// ============================================
// TEXTURE SELECTION MODAL
// ============================================
const TextureSelectionModal = ({ textures, onSelect, onClose, isMobile }) => {
  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.85)',
        zIndex: 2000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: isMobile ? '0' : '40px',
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: 'white',
          borderRadius: isMobile ? '0' : '20px',
          width: '100%',
          maxWidth: isMobile ? '100%' : '800px',
          height: isMobile ? '100vh' : 'auto',
          maxHeight: isMobile ? '100vh' : '80vh',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: isMobile ? 'none' : '0 25px 60px rgba(0,0,0,0.3)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '16px 20px',
            borderBottom: '1px solid #eaeaea',
            flexShrink: 0,
            background: 'white',
          }}
        >
          <h3
            style={{
              fontFamily: 'Avenir, sans-serif',
              fontWeight: 600,
              fontSize: '1rem',
              color: '#333',
              margin: 0,
            }}
          >
            Select a Texture
          </h3>
          <button
            onClick={onClose}
            style={{
              background: 'rgba(0,0,0,0.05)',
              border: 'none',
              borderRadius: '50%',
              width: '36px',
              height: '36px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              color: '#666',
              fontSize: '18px',
            }}
          >
            <FiX size={20} />
          </button>
        </div>

        {/* Texture Grid */}
        <div
          style={{
            flex: 1,
            overflowY: 'auto',
            padding: '20px',
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
            gap: '12px',
            alignContent: 'start',
          }}
        >
          {textures.length === 0 ? (
            <div
              style={{
                gridColumn: '1 / -1',
                textAlign: 'center',
                padding: '40px',
                fontFamily: 'Avenir, sans-serif',
                color: '#999',
              }}
            >
              No textures found. Generate a texture first.
            </div>
          ) : (
            textures.map((texture) => (
              <div
                key={texture.id}
                onClick={() => onSelect(texture)}
                style={{
                  cursor: 'pointer',
                  borderRadius: '12px',
                  overflow: 'hidden',
                  border: '2px solid transparent',
                  transition: 'all 0.2s ease',
                  background: '#f5f5f5',
                  ':hover': { borderColor: '#88b7ff' },
                }}
                onMouseEnter={(e) => (e.currentTarget.style.borderColor = '#88b7ff')}
                onMouseLeave={(e) => (e.currentTarget.style.borderColor = 'transparent')}
              >
                <div style={{ aspectRatio: '1/1', overflow: 'hidden', background: '#e0e0e0' }}>
                  <img
                    src={texture.imageUrl}
                    alt={texture.shortDescription || 'Texture'}
                    style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                    loading="lazy"
                  />
                </div>
                <div style={{ padding: '8px', background: 'white' }}>
                  <div
                    style={{
                      fontFamily: 'Avenir, sans-serif',
                      fontSize: '0.7rem',
                      fontWeight: 600,
                      color: '#333',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {texture.shortDescription || texture.prompt?.substring(0, 40) || 'Texture'}
                  </div>
                  <div style={{ fontSize: '0.6rem', color: '#aaa', marginTop: '2px' }}>
                    {texture.model || 'AI'} · 1:1
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

// ============================================
// DEV STUDIO - TEXTURE-TO-SCENE GENERATION
// ============================================
const DevStudio = ({ 
  authUser, 
  onClose,
  imagesRemaining,
  videosRemaining,
  updateRemainingCounts,
  isPWA
}) => {
  
 const { showToast, showConfirm, showFullScreenModal } = useNotification(); 
  // ============================================
  // STATE MANAGEMENT
  // ============================================
  
  // Workflow State
  const [currentStep, setCurrentStep] = useState('texture'); // 'texture' | 'scene' | 'result'
  
  // Texture States
  const [textureShortPrompt, setTextureShortPrompt] = useState('');
  const [textureExpandedPrompt, setTextureExpandedPrompt] = useState('');
  const [textureGuidelines, setTextureGuidelines] = useState(null);
  const [generatedTexture, setGeneratedTexture] = useState(null);
  const [isExpandingTexture, setIsExpandingTexture] = useState(false);
  const [isGeneratingTexture, setIsGeneratingTexture] = useState(false);
  const [includeTechnique, setIncludeTechnique] = useState(false);
  const [showTextureSuccess, setShowTextureSuccess] = useState(false);
  
  // Scene States
  const [sceneShortPrompt, setSceneShortPrompt] = useState('');
  const [sceneExpandedPrompt, setSceneExpandedPrompt] = useState('');
    const [upscaleEnabled, setUpscaleEnabled] = useState(false);
  const [selectedCameraAngle, setSelectedCameraAngle] = useState('chest-high-downward'); // default original angle
  const [includeRug, setIncludeRug] = useState(false);
  const [includePlants, setIncludePlants] = useState(false);
  const [includeFlooring, setIncludeFlooring] = useState(false);
  const [includeArt, setIncludeArt] = useState(false);
  const [includePaintedWalls, setIncludePaintedWalls] = useState(false);
  const [includeCeiling, setIncludeCeiling] = useState(false);
  const [includeMoulding, setIncludeMoulding] = useState(false);
  const [includeLighting, setIncludeLighting] = useState(false);
  const [includeDoor, setIncludeDoor] = useState(false);
  const [includeDecor, setIncludeDecor] = useState(false);
  const [includeHardware, setIncludeHardware] = useState(false);
  const [isUpscaling, setIsUpscaling] = useState(false);
  const [sceneGuidelines, setSceneGuidelines] = useState(null);
  const [isExpandingScene, setIsExpandingScene] = useState(false);
  const [isGeneratingScene, setIsGeneratingScene] = useState(false);
  const [aspectRatio, setAspectRatio] = useState('9:16');
  const [imageSize, setImageSize] = useState('4K');
  const [generatedScene, setGeneratedScene] = useState(null);
  
  // Gallery States
  const [galleryFilter, setGalleryFilter] = useState('all');
  const [sortOrder, setSortOrder] = useState('desc'); // 'desc' = newest first, 'asc' = oldest first
  const [generatedImages, setGeneratedImages] = useState([]);
  const [imagesLoading, setImagesLoading] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [showImageDetails, setShowImageDetails] = useState(false);
  const [isTextureSelectionMode, setIsTextureSelectionMode] = useState(false);
  const [enableGlobalRealism, setEnableGlobalRealism] = useState(false);   // Apply Kling‑style realism to all models
  
  // Bulk Download States
  const [selectedImageIds, setSelectedImageIds] = useState(new Set());
  const [isBulkDownloading, setIsBulkDownloading] = useState(false);
  const [showBulkSelect, setShowBulkSelect] = useState(false);
  const [displayCount, setDisplayCount] = useState(12); // lazy render gallery

  
  // UI States
  const [selectedTextureModel, setSelectedTextureModel] = useState(models[2]); // Seedream 4.5 (default for textures)
  const [selectedSceneModel, setSelectedSceneModel] = useState(models[3]);     // Kling V3 (default for scenes)

  const [showModelDropdown, setShowModelDropdown] = useState(false);
  const [generationStatus, setGenerationStatus] = useState('');
  const [isDownloading, setIsDownloading] = useState(false);
  const [activeGuidelineTab, setActiveGuidelineTab] = useState('prompt'); // 'prompt' | 'guidelines'
  
  // User data for credit checking
  const [userData, setUserData] = useState(null);

    // ============================================
  // ASSET STUDIO STATE
  // ============================================
  const [assetPrompt, setAssetPrompt] = useState('');
  const [assetImageUrl, setAssetImageUrl] = useState(null);
  const [isGeneratingAsset, setIsGeneratingAsset] = useState(false);
  const [assetGenerationStatus, setAssetGenerationStatus] = useState('');
  const [assetSize, setAssetSize] = useState('4K');
  const [selectedAssetModel, setSelectedAssetModel] = useState(null);
  const [assetAspectRatio, setAssetAspectRatio] = useState('9:16');
  
  // Asset-specific models (only Segmind + Google Nano Banana)
  const assetModels = [
    {
      id: 'nanobanana',
      name: 'Nano Banana Pro',
      endpoint: 'nano-banana-pro',
      description: 'Google model, fast generation',
      defaultAspectRatio: '9:16',
      defaultSize: '4K',
      outputFormat: 'jpg',
      pricing: 0.15,
      supportedAspectRatios: ['1:1', '9:16', '16:9', '3:4', '4:3'],
    },
    {
      id: 'segmind',
      name: 'Segmind Vega',
      endpoint: 'segmind-vega', // Replace with actual endpoint
      description: 'Segmind text-to-image',
      defaultAspectRatio: '9:16',
      defaultSize: '4K',
      outputFormat: 'png',
      pricing: 0.035,
      supportedAspectRatios: ['1:1', '9:16', '16:9', '3:4', '4:3'],
    },
  ];


  // ============================================
  // PRODUCTION & LOCAL API URL HELPER
  // ============================================
  // Segmind proxy endpoint (new Netlify function)
  // Returns the base URL for Segmind API calls.
  // In dev we call Segmind directly (no proxy), in production we use the Netlify function.
    //Make sure YOU SET UP A nginx SERVER URGENT IMPORTANT DO NOT SKIP FOR PRODUCTION DEPLOYMENT//
  const getSegmindBaseUrl = () => {
    if (import.meta.env.DEV) return 'https://api.segmind.com/v1';
    return '/api/segmind';
  };


  // Helper to add the Authorization header in dev (the proxy handles it in prod)
  const getSegmindAuthHeaders = (extraHeaders = {}) => {
    const headers = { ...extraHeaders };
    // In production, nginx adds the API key – we never send it from the browser
    if (import.meta.env.DEV) {
      headers['x-api-key'] = import.meta.env.VITE_SEGMIND_API_KEY;
    }
    return headers;
  };
    //Make sure YOU SET UP A nginx SERVER URGENT IMPORTANT DO NOT SKIP FOR PRODUCTION DEPLOYMENT//
    
  // Helper to create a fetch with timeout
  const fetchWithTimeout = (url, options = {}, timeoutMs = 30000) => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
    return fetch(url, { ...options, signal: controller.signal }).finally(() => clearTimeout(timeoutId));
  };

  // Poll for v2 async job result, returns a blob
  const pollForResult = async (requestId, maxAttempts = 30, interval = 5000) => {
    const startTime = Date.now();
    const totalTimeout = 300000; // 5 minutes total

    for (let i = 0; i < maxAttempts; i++) {
      if (Date.now() - startTime > totalTimeout) {
        throw new Error('Polling timed out after 5 minutes');
      }

      await new Promise(resolve => setTimeout(resolve, interval));

      const pollUrl = import.meta.env.DEV
        ? `https://api.segmind.com/v2/requests/${requestId}`
        : `${getSegmindBaseUrl()}?model=v2-poll&request_id=${encodeURIComponent(requestId)}`;

      const pollResponse = await fetch(pollUrl, {
        method: 'GET',
        headers: getSegmindAuthHeaders(),
      });

      if (!pollResponse.ok) {
        const err = await pollResponse.text();
        throw new Error(`Poll error ${pollResponse.status}: ${err}`);
      }

      const data = await pollResponse.json();

      if (data.status === 'COMPLETED' && data.output) {
        const imageUrl = data.output.url || data.output;
        if (!imageUrl) throw new Error('No output URL in completed response');
        const imageResp = await fetch(imageUrl);
        if (!imageResp.ok) throw new Error(`Failed to download image: ${imageResp.status}`);
        return await imageResp.blob();
      }

      if (data.status === 'FAILED') {
        throw new Error('Generation failed on Segmind side');
      }
      // else still processing
    }
    throw new Error('Polling retries exhausted');
  };

  // Refs
  const sceneInputRef = useRef(null);
  const modelDropdownRef = useRef(null);
  const isMobile = typeof window !== 'undefined' && window.innerWidth <= 767;
  
  // Initialize Replicate client (for image-to-text)
  const replicateClient = useRef(null);
  
  // (No Replicate client needed – all models now go through Segmind proxy)
  
  // ============================================
  // IMAGE TO TEXT (VISION) FUNCTION
  // ============================================
  // This function uses deepseek-ai/deepseek-vl2 to analyze an image and return a description
  // It's ready to be connected to UI elements on another page
  const imageToText = async (imageUrl, prompt) => {
    if (!replicateClient.current) {
      console.error('Replicate client not initialized');
      return null;
    }
    
    try {
      const input = {
        image: imageUrl,
        prompt: prompt || "Describe this image in detail, including colors, objects, textures, and composition."
      };
      
      const output = await replicateClient.current.run(
        "deepseek-ai/deepseek-vl2:e5caf557dd9e5dcee46442e1315291ef1867f027991ede8ff95e304d4f734200",
        { input }
      );
      
      console.log('Image-to-text output:', output);
      return output;
    } catch (error) {
      console.error('Image-to-text error:', error);
      return null;
    }
  };
  
  // Helper function to convert a local file/image blob to a URL for the API
  const imageBlobToUrl = (blob) => {
    return URL.createObjectURL(blob);
  };
  
  // Helper function to analyze a texture image and generate a description
  const analyzeTextureImage = async (imageUrl) => {
    const prompt = "Describe this texture in detail. What material does it look like? What colors, patterns, and surface characteristics does it have? Is it rough, smooth, glossy, or matte?";
    return await imageToText(imageUrl, prompt);
  };
  
  // Helper function to analyze a scene image and generate a description
  const analyzeSceneImage = async (imageUrl) => {
    const prompt = "Describe this interior scene or room. What furniture, objects, lighting conditions, and spatial layout do you see? What style or mood does the room convey? Be specific about colors, materials, and composition.";
    return await imageToText(imageUrl, prompt);
  };
  
  // Green Key Window Text (constant)
  // GREEN KEY: Disabled for now. To re-enable, uncomment the line below and comment out the empty string.
  // const greenKeyText = "All window glass is solid flat chroma-key green (RGB 0,255,0) with no reflections, no glare, no transparency, perfectly keyable.";
  const greenKeyText = ""; // Green key disabled
  
  // Available aspect ratios
  const getAspectRatios = () => {
    if (selectedSceneModel.id === 'nanobanana' || selectedSceneModel.id === 'recraft') {
      return ['9:16', '16:9', '1:1', '4:3', '3:4'];
    }
    if (selectedSceneModel.id === 'seedream') {
      return ['16:9', '9:16', '1:1', '4:3', '3:4'];
    }
    return ['1:1', '16:9', '9:16'];
  };
  
  const getSizes = () => {
    if (selectedSceneModel.id === 'seedream') {
      return ['8K', '4K', '2K', '1080p'];
    }
    return ['1024x1024', '768x768', '512x512'];
  };
  
  // ============================================
  // REAL-TIME GALLERY SUBSCRIPTION
  // ============================================
  // Fetch user data for credit checking
  useEffect(() => {
    if (!authUser) return;
    const fetchUserData = async () => {
      const userRef = doc(db, 'users', authUser.uid);
      const userSnap = await getDoc(userRef);
      if (userSnap.exists()) {
        setUserData(userSnap.data());
      }
    };
    fetchUserData();
  }, [authUser]);

  // Gallery subscription with automatic silent cleanup
  useEffect(() => {
    if (!authUser) return;
    
    setImagesLoading(true);
    
    const imagesRef = collection(db, 'marketing_images');
    const q = query(imagesRef, where('userId', '==', authUser.uid), orderBy('createdAt', sortOrder));
    
    const unsubscribe = onSnapshot(q, 
      (snapshot) => {
        const images = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        
        // Filter out images with invalid data but DON'T auto-delete
        const validImages = images.filter(image => {
          if (!image.id) {
            console.log('Filtered out: missing id');
            return false;
          }
          if (!image.imageUrl) {
            console.log('Filtered out: missing imageUrl for', image.id);
            return false;
          }
          if (!image.imageUrl.startsWith('http')) {
            console.log('Filtered out: invalid imageUrl for', image.id);
            return false;
          }
          return true;
        });
        
        // Log invalid images for debugging but don't delete
        const invalidCount = images.length - validImages.length;
        if (invalidCount > 0) {
          console.log(`Found ${invalidCount} images with invalid data. They will still show in gallery as broken.`);
        }
        
        setGeneratedImages(validImages);
        setImagesLoading(false);
      }, 
      (error) => {
        console.error('Error fetching images:', error);
        setImagesLoading(false);
      }
    );
    
    return () => unsubscribe();
  }, [authUser, sortOrder]);
  
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modelDropdownRef.current && !modelDropdownRef.current.contains(event.target)) {
        setShowModelDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  // ============================================
  // AUTO-INJECT TEXTURE INTO SCENE DESCRIPTION
  // ============================================
  const autoInjectTextureIntoScene = useCallback((textureName) => {
    if (!textureName) return;
    
    const texturePlaceholder = `[TEXTURE:${textureName}]`;
    
    setSceneShortPrompt(prev => {
      if (!prev.trim()) {
        return '';
      }
      
      if (prev.includes('[TEXTURE:')) {
        return prev.replace(/\[TEXTURE:[^\]]+\]/, texturePlaceholder);
      }
      
      const surfacePatterns = /(coffee table|table|surface|countertop|desk|floor|wall|ceiling|island|bar|shelf|mantel|vanity)/gi;
      
      if (surfacePatterns.test(prev)) {
        return prev.replace(surfacePatterns, (match) => `${match} made of ${texturePlaceholder}`);
      }
      
      return `${prev} Featuring ${texturePlaceholder}.`;
    });
    
    setGenerationStatus(`✓ Texture "${textureName}" is ready! Click "Insert Texture" in the scene step to place it.`);
    setTimeout(() => setGenerationStatus(''), 3000);
  }, []);
  
  // ============================================
  // USE TEXTURE FROM GALLERY
  // ============================================
  const useTextureFromGallery = (image) => {
    const textureData = {
      id: image.id,
      shortDescription: image.shortDescription || image.prompt?.substring(0, 100) || 'Selected texture',
      prompt: image.prompt,
      imageUrl: image.imageUrl,
      storagePath: image.storagePath
    };
    
    setGeneratedTexture(textureData);
    setTextureShortPrompt(image.shortDescription || image.prompt?.substring(0, 100) || 'Selected texture');
    setTextureExpandedPrompt(image.prompt || '');
    
    const textureName = image.shortDescription || 'selected texture';
    autoInjectTextureIntoScene(textureName);
    
    setIsTextureSelectionMode(false);
    setGenerationStatus('✓ Texture selected & injected into scene!');
    setTimeout(() => setGenerationStatus(''), 2000);
  };
  
  // ============================================
  // RANDOM TEXTURE GENERATOR
  // ============================================
  // Uses: COLOR_LIBRARY, CUSTOM_COLORS, TEXTURE_MATERIAL_LIBRARY, CUSTOM_MATERIALS, TECHNIQUE_LIBRARY
  // Output structured format: "material:<material> in color:<color>", optionally prefixed with "technique:<technique> "
  const generateRandomTexture = async () => {
    try {
      const colors = [...COLOR_LIBRARY, ...CUSTOM_COLORS];
      const materials = [...TEXTURE_MATERIAL_LIBRARY, ...CUSTOM_MATERIALS];
      const colorEntry = colors[Math.floor(Math.random() * colors.length)];
      const materialEntry = materials[Math.floor(Math.random() * materials.length)];
      
      let description = `(Material: ${stripLabel(materialEntry)}) in (Color: ${stripLabel(colorEntry)})`;
      
      if (includeTechnique) {
        const techniqueEntry = TECHNIQUE_LIBRARY[Math.floor(Math.random() * TECHNIQUE_LIBRARY.length)];
        description = `(Technique: ${stripLabel(techniqueEntry)}) ${description}`;
      }
      
      setTextureShortPrompt(description);
      setGenerationStatus('✓ Random texture generated! Expand it or generate directly.');
      setTimeout(() => setGenerationStatus(''), 3000);
    } catch (error) {
      console.error('Random texture error:', error);
      showToast('Failed to generate random texture.', 'error');
    }
  };
  // ============================================
  // RANDOM SCENE GENERATOR (full room description)
  // ============================================
  // Uses all applicable categories: SOFA_SHAPE_LIBRARY, FURNITURE_TYPE_LIBRARY, LIGHTING_FIXTURE_LIBRARY,
  // WINDOW_LIBRARY, DOOR_LIBRARY, MOULDING_LIBRARY, CEILING_LIBRARY, FLOORING_LIBRARY, HARDWARE_LIBRARY,
  // PLANT_LIBRARY, DECOR_LIBRARY, WALL_ART_LIBRARY, TECHNIQUE_LIBRARY and custom collections.
  const generateRandomScene = async () => {
    setIsExpandingScene(true);
    setGenerationStatus('Building a random interior scene…');

    try {
      const options = {
        includeRug,
        includeFlooring,
        includePlants: true, // plants are always included by default
        includeArt,
        includePaintedWalls,
        includeCeiling,
        includeMoulding,
        includeLighting,
        includeDoor,
        includeDecor,
        includeTechnique,
        includeHardware,
      };

      const sceneDescription = buildRandomSceneDescription(options);

      // Auto-inject texture placeholder if a texture exists
      let finalDescription = sceneDescription;
      if (generatedTexture) {
        const textureName = textureShortPrompt || generatedTexture.shortDescription;
        finalDescription = finalDescription + ` Featuring [TEXTURE:${textureName}].`;
      }

      setSceneShortPrompt(finalDescription);
      setGenerationStatus('✓ Random scene ready! Expand or generate.');
      setTimeout(() => setGenerationStatus(''), 3000);
    } catch (error) {
      console.error('Random scene error:', error);
      showToast('Failed to generate random scene.', 'error');
    } finally {
      setIsExpandingScene(false);
    }
  };
  
  // ============================================
  // REMOVE TEXTURE FROM SCENE DESCRIPTION
  // ============================================
  const removeTextureFromSceneDescription = () => {
    setSceneShortPrompt(prev => {
      return prev.replace(/\[TEXTURE:[^\]]+\]/g, '').replace(/\s+/g, ' ').trim();
    });
  };
  
  // ============================================
  // INSERT TEXTURE PLACEHOLDER AT CURSOR
  // ============================================
  const insertTexturePlaceholderAtCursor = () => {
    if (!generatedTexture) {
      showToast('Please generate or select a texture first', 'warning');
      return;
    }
    
    const textureName = textureShortPrompt || generatedTexture.shortDescription;
    const placeholder = `[TEXTURE:${textureName}]`;
    
    if (sceneInputRef.current) {
      const textarea = sceneInputRef.current;
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const text = sceneShortPrompt;
      
      const newText = text.substring(0, start) + placeholder + text.substring(end);
      setSceneShortPrompt(newText);
      
      setTimeout(() => {
        textarea.focus();
        textarea.selectionStart = start + placeholder.length;
        textarea.selectionEnd = start + placeholder.length;
      }, 0);
    } else {
      autoInjectTextureIntoScene(textureName);
    }
  };
  
  // ============================================
  // EXPAND TEXTURE PROMPT WITH DETAILED GUIDELINES
  // ============================================
   const expandTexturePrompt = async () => {
    if (!textureShortPrompt.trim()) {
      showToast('Please enter a texture description first', 'warning');
      return;
    }
    
    setIsExpandingTexture(true);
    setGenerationStatus('AI is analyzing and expanding your texture description...');
    
    const expandCost = 1;
    const hasCredits = await checkSufficientCredits(userData, expandCost, 'image');
    if (!hasCredits.hasCredits) {
      showToast(`Insufficient credits. Need ${expandCost}, have ${hasCredits.remaining}.`, 'error');
      setIsExpandingTexture(false);
      return;
    }
    
    try {
      const systemPrompt = `You are a material design expert. Given a short texture description, provide a comprehensive breakdown.

SHORT DESCRIPTION: "${textureShortPrompt}"

CRITICAL REALISM RULES:
- The texture must be matte or very softly reflective – NO gloss, NO high specular highlights, NO plastic shine.
- Surfaces contain subtle micro‑imperfections: fine grain, pores, hairline scratches, fabric weave, wood grain, or chalky variations.
- The albedo (colour) is completely separated from lighting – no baked shadows, no ambient occlusion, no directional shading.
- The surface must appear as a perfectly flat, orthographic top‑down material sample with zero perspective distortion, uniform scale, even neutral illumination, and edge‑to‑edge coverage.
- No context, no background, no edges, no depth blur.

OUTPUT FORMAT (strictly follow this structure, output ONLY valid JSON):
{
  "expandedPrompt": "A perfectly flat, orthogonal top‑down view of a seamless, full‑frame surface of … [detailed description including roughness and micro‑texture] … The texture fills the entire frame edge‑to‑edge with full‑frame sharpness at 8K resolution. Absolutely no depth of field blur — every detail from corner to corner is perfectly sharp and in focus. No background, no context, no edges.",
  "guidelines": {
    "style": "Describe the overall style/aesthetic in 1‑2 sentences, noting its matte/soft feel",
    "colors": "List the specific colors and their roles (e.g., 'Cream base (#F5F0E8), dark brown veins (#3C2415), subtle gold flecks')",
    "materials": "Describe the material composition, physical properties, and surface roughness level",
    "details": "List key visual details, patterns, micro‑imperfections, and how light interacts (Fresnel at glancing angles only)",
    "bestUses": "Suggest 2‑3 ideal surfaces or objects this texture would work best on"
  }
}

RULES:
- The expandedPrompt must be a single flowing paragraph
- Explicitly mention micro‑roughness, matte finish, and absence of gloss
- No markdown, no line breaks in the prompt
- Guidelines should be specific and actionable
- Colors should include hex codes where possible`;

      const response = await fetchWithTimeout(`${getSegmindBaseUrl()}/deepseek-chat`, {
        method: 'POST',
        headers: getSegmindAuthHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          model: 'deepseek-chat',
          messages: [{ role: 'user', content: systemPrompt }],
        }),
      }, 60000);

      if (!response.ok) {
        const errText = await response.text();
        throw new Error(`DeepSeek Chat error ${response.status}: ${errText}`);
      }

      const completion = await response.json();
      const rawOutput = completion.choices?.[0]?.message?.content;
      if (!rawOutput) throw new Error('Empty response from AI');

      let parsedOutput;
      try {
        const jsonMatch = rawOutput.match(/\{[\s\S]*\}/);
        if (jsonMatch) {
          parsedOutput = JSON.parse(jsonMatch[0]);
        } else {
          throw new Error('No JSON found');
        }
      } catch (parseError) {
        console.warn('Failed to parse JSON, using fallback format');
        parsedOutput = {
          expandedPrompt: rawOutput.trim(),
          guidelines: {
            style: `Based on: ${textureShortPrompt}`,
            colors: 'See expanded prompt for details',
            materials: 'See expanded prompt for details',
            details: 'See expanded prompt for details',
            bestUses: 'Coffee tables, countertops, accent surfaces'
          }
        };
      }

      setTextureExpandedPrompt(parsedOutput.expandedPrompt);
      setTextureGuidelines(parsedOutput.guidelines);

      await deductImageCredits(authUser.uid, expandCost, { type: 'texture_expansion' });
      if (updateRemainingCounts) updateRemainingCounts();

      setGenerationStatus('✓ Texture expanded successfully with detailed guidelines!');
      setTimeout(() => setGenerationStatus(''), 3000);
    } catch (error) {
      console.error('Texture expansion error:', error);
      setGenerationStatus('Expansion failed. Please try again.');
      showToast('Failed to expand texture prompt. Please try again.', 'error');
    } finally {
      setIsExpandingTexture(false);
    }
  };
  
  // ============================================
  // GENERATE TEXTURE IMAGE
  // ============================================
  const generateTextureImage = async () => {
    if (!textureExpandedPrompt.trim()) {
      showToast('Please expand your texture description first', 'warning');
      return;
    }
    
    if (textureExpandedPrompt.length > 2000) {
      showToast(`Your prompt is ${textureExpandedPrompt.length} characters. Please shorten it to 2000.`, 'warning');
      return;
    }
    
    const model = selectedTextureModel;
    const cost = typeof model.pricing === 'object' ? model.pricing[model.defaultSize] || 0.15 : model.pricing || 0.035;
    
    setIsGeneratingTexture(true);
    setGenerationStatus(`Generating texture with ${model.name}...`);

    const result = await safeGenerate(
      authUser.uid,
      cost,
      async (onProgress) => {
        let finalTexturePrompt = 'sharp focus, no depth of field, everything in focus, ' + textureExpandedPrompt;
        if (enableGlobalRealism || model.id === 'kling') {
          finalTexturePrompt = SHARED_POSITIVE_PROMPT + ' ' + finalTexturePrompt;
          finalTexturePrompt += KLING_TEXTURE_GRAIN_SUFFIX;
        }
        const body = {
          model: model.endpoint,
          prompt: finalTexturePrompt,
        };

        // Kling texture overrides
        if (model.id === 'kling') {
          body.cfg_scale = 4.8;
          body.num_inference_steps = 28;
          body.negative_prompt = SHARED_NEGATIVE_PROMPT;
          body.creativity = 0.4;
          body.output_format = 'png';
        }

        if (model.id === 'recraft') {
          body.size = '1024x1024';
          body.style = 'any';
        } else if (model.id === 'nanobanana') {
          body.aspect_ratio = '1:1';
          body.output_format = 'jpg';
          body.output_resolution = '4K';
        } else if (model.id === 'seedream') {
          body.aspect_ratio = '1:1';
          body.size = '2K';
          body.max_images = 1;
          body.watermark = false;
          body.optimize_prompt = 'standard';
        } else if (model.id === 'kling') {
          body.aspect_ratio = '1:1';
          body.resolution = '2K';
          body.output_format = 'png';
        }

                // Use v2 async for long‑running models
        // Always use v2 endpoint – it may return JSON (async) or binary (sync)
        const slug = model.endpoint;
        const requestBody = { model: 'v2-submit', slug, ...body };
        const url = import.meta.env.DEV
          ? `https://api.segmind.com/v2/${slug}`
          : getSegmindBaseUrl();

        // Increased timeout – the proxy itself may take a few seconds to submit
        const response = await fetchWithTimeout(url, {
          method: 'POST',
          headers: getSegmindAuthHeaders({ 'Content-Type': 'application/json' }),
          body: JSON.stringify(requestBody),
        }, 120000); // 2 minutes

        if (!response.ok) {
          const errText = await response.text();
          throw new Error(`Server error ${response.status}: ${errText}`);
        }

        const contentType = response.headers.get('content-type') || '';
        let imageBlob;

        if (contentType.includes('application/json')) {
          // Async job – parse JSON, get request_id, then poll
          const data = await response.json();
          if (!data.request_id) throw new Error('No request_id returned from Segmind v2');
          imageBlob = await pollForResult(data.request_id);
        } else {
          // Sync response – the image binary is returned directly
          imageBlob = await response.blob();
        }

        const reader = new FileReader();
        const base64Image = await new Promise((resolve, reject) => {
          reader.onloadend = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(imageBlob);
        });

        const storage = getStorage();
        const timestamp = Date.now();
        const storagePath = `users/${authUser.uid}/textures/${timestamp}.${model.outputFormat || 'jpg'}`;
        const storageRef = ref(storage, storagePath);
        await uploadString(storageRef, base64Image, 'data_url');
        const permanentUrl = await getDownloadURL(storageRef);

        const textureData = {
          id: timestamp.toString(),
          userId: authUser.uid,
          shortDescription: textureShortPrompt,
          prompt: textureExpandedPrompt,
          guidelines: textureGuidelines,
          type: 'texture',
          imageUrl: permanentUrl,
          storagePath: storagePath,
          model: model.name,
          modelId: model.id,
          createdAt: serverTimestamp(),
        };

        return { textureData };
      },
      {
        type: 'image',
        onStart: () => {},
        onProgress: () => {},
        onComplete: (result) => {
          const { textureData } = result;
          setGeneratedTexture(textureData);
          setShowTextureSuccess(true);
          autoInjectTextureIntoScene(textureShortPrompt);
          setGenerationStatus('✓ Texture generated & ready for scene!');
          showToast(`Texture "${textureShortPrompt}" created!`, 'success');
          setTimeout(() => setShowTextureSuccess(false), 4000);
        },
        onError: (error) => {
          setGenerationStatus('Texture generation failed. Please try again.');
          showToast('Failed to generate texture. Please try again.', 'error');
        }
      }
    );

    if (result.success) {
      if (result.result.textureData && result.result.textureData.imageUrl && result.result.textureData.imageUrl.startsWith('http')) {
        const texturesRef = collection(db, 'marketing_images');
        await addDoc(texturesRef, result.result.textureData);
      } else {
        console.error('Invalid texture data - not saving to database:', result.result.textureData);
        showToast('Generated texture is invalid and will not be saved. Please try again.', 'error');
      }
      if (updateRemainingCounts) updateRemainingCounts();
    }

    setIsGeneratingTexture(false);
  };
  
  // ============================================
// EXPAND SCENE PROMPT WITH GUIDELINES (SMARTPHONE PHOTO STYLE)
// ============================================
  const expandScenePrompt = async () => {
    const cleanSceneDescription = sceneShortPrompt.replace(/\[TEXTURE:[^\]]+\]/g, (match) => {
      const textureName = match.match(/\[TEXTURE:([^\]]+)\]/)[1];
      return textureName;
    });

    if (!cleanSceneDescription.trim()) {
      showToast('Please enter a scene description first', 'warning');
      return;
    }

    if (!generatedTexture) {
      showToast('Please generate or select a texture first', 'warning');
      return;
    }

    setIsExpandingScene(true);
    setGenerationStatus('AI is composing your scene with ultra‑wide smartphone style...');

    // Check credits before expanding scene
    const expandSceneCost = 2; // Scene expansion costs 2 credits (more complex)
    const hasCredits = await checkSufficientCredits(userData, expandSceneCost, 'image');
    if (!hasCredits.hasCredits) {
      showToast(`Insufficient credits. Need ${expandSceneCost}, have ${hasCredits.remaining}.`, 'error');
      setIsExpandingScene(false);
      return;
    }

    const fullTextureDescriptionRaw = textureExpandedPrompt || generatedTexture.prompt || textureShortPrompt;
    let fullTextureDescription = fullTextureDescriptionRaw;
    fullTextureDescription = fullTextureDescription
      .replace(/^A perfectly flat, orthogonal top-down view of a seamless, full-frame surface of\s*/i, '')
      .replace(/\s*The texture fills the entire frame edge-to-edge with full-frame sharpness at 8K resolution\.?\s*No background, no context, no edges\.?/gi, '')
      .replace(/\s*Organic shadow patterns are baked directly into the surface as flat 2D texture information, with no implied light direction, no dimensional cast, no volumetric depth\.?/gi, '')
      .replace(/\s*Soft, diffuse shadow clusters are distributed across the lower portion, and a sharper organic shadow pattern cuts diagonally across the upper-right portion\.?/gi, '')
      .trim();
    // Use full description if reasonably short, otherwise truncate to 300 chars to preserve detail
    if (fullTextureDescription.length > 300) {
      fullTextureDescription = fullTextureDescription.substring(0, 300);
      const lastPeriod = fullTextureDescription.lastIndexOf('.');
      const lastSpace = fullTextureDescription.lastIndexOf(' ');
      const cutPoint = lastPeriod > 150 ? lastPeriod + 1 : (lastSpace > 150 ? lastSpace : 300);
      fullTextureDescription = fullTextureDescription.substring(0, cutPoint).trim();
    }
    if (!fullTextureDescription || fullTextureDescription.length < 20) {
      fullTextureDescription = textureShortPrompt;
    }
    const textureName = textureShortPrompt;

    // ========== DYNAMIC CAMERA ANGLE (selected by user) ==========
    const currentAngle = cameraAngles.find(a => a.id === selectedCameraAngle) || cameraAngles[0];
    const forcedCameraInstruction = currentAngle.instruction;

    const forcedLightingInstruction = `Lighting uses a hybrid smartphone computational technique: walls and ceiling receive extremely soft, diffused, milky, low‑contrast light with no harsh shadow edges. **Contrast reduction on flat surfaces:** Walls, ceiling, and large floor areas have extremely low local contrast — micro‑contrast is suppressed, tonal transitions are smooth and flat, avoiding any sense of harsh definition or 3D rendering sharpness. Wall and ceiling brightness is intentionally reduced — highlights are pulled down to a subdued light gray‑beige (RGB 190‑210), and these surfaces appear slightly underexposed compared to the rest of the scene, never bright or glowing. Furniture, floor details, and textured objects receive an extremely soft, diffuse, shadowless light – as if the room is lit by a giant, pure white diffusion panel with zero directionality. Shadows are extremely faint and soft, edges are very gentle, and surfaces appear uniformly matte and low‑contrast. The brightest points on furniture are soft and diffused, blending naturally without harsh whites. **Ceiling restriction (absolute):** The ceiling must be a normal, purely architectural residential ceiling. Absolutely no LED panels, no flat glowing ceiling tiles, no luminous ceiling surfaces, no strip lighting, no under‑lighting, no neon accents, no sci‑fi designs. Acceptable ceilings: simple flat drywall, subtle crown molding, vaulted or cathedral ceilings with exposed wooden beams, recessed skylights that appear as structural openings to the sky (not light fixtures), or small unobtrusive recessed downlights (circular, not square panels). If any lights are present on the ceiling, they must be discreet recessed spots or a simple pendant fixture—never a panel, never a glowing element. The overall atmosphere remains contemporary, neutral, and realistic, with the ceiling appearing as a normal architectural surface, not a light source. Processed with Smart HDR multi‑exposure fusion. Highlights are never clipped — brightest areas on furniture, windows, or lamps peak at soft cream (RGB 220‑235). Shadows are lifted — darkest areas stay above RGB 30‑40, retaining visible grain and fabric weave. Local tone mapping exposes every region independently: a dark corner and a bright window both show full detail. Soft, diffused specular highlights on glossy surfaces. No harsh shadows, no crushed blacks.`;

    const smartphoneQualityModifiers = `Smartphone photo with minimal computational processing: light noise reduction that preserves micro‑texture, no aggressive smoothing. Surfaces show natural fabric weave, wood grain, and subtle imperfections. Moderate, invisible sharpening only on edges. Hyperfocal depth of field keeps everything in focus. Contrast curve: overall scene contrast is low, with deep blacks lifted to dark gray (RGB 30‑40) and whites compressed to pale cream (RGB 220‑235), mimicking a raw sensor capture. Colors are slightly desaturated, white balance neutral at 5000‑5500K. The image avoids looking like a 3D render — it feels like an unedited straight‑from‑camera photo.`;

    // ========== MATERIAL REALISM ==========
    const materialRealismInstruction = `MATERIAL REALISM (CRITICAL):
- The scene is low‑contrast and matte, lit by soft, natural window light. No plastic, no gloss, no CGI shine.
- The texture "${fullTextureDescription}" applied to furniture must show visible, physical fabric weave or wood grain. The surface is slightly rough, like unpolished stone or raw linen, with micro‑shadows in the creases.
- Fabric: visible threads, slight unevenness in tufting, natural drape without sharp folds. Wood: visible open pores and tiny knots, subtle grain variations, completely matte.
- Shadows are soft but present – furniture casts gentle contact shadows on the floor.
- Colors are muted and natural, with no pure white or pure black. The overall feel is a raw, unedited smartphone photo with real‑world texture.`;

    try {
      const systemPrompt = `You are an expert architectural interior photographer and AI image generation prompt engineer. Your task is to take a short description of an interior scene with a specified texture, and produce a comprehensive, highly detailed prompt that results in a realistic 9:16 vertical smartphone photo.

USER SCENE DESCRIPTION: "${cleanSceneDescription}"
TEXTURE TO APPLY (IMPORTANT – must be described fully in the final prompt): "${fullTextureDescription}"

${forcedCameraInstruction}
${forcedLightingInstruction}
${smartphoneQualityModifiers}
${materialRealismInstruction}

ACCESSORY REQUIREMENTS (strictly enforce based on user selection):
- Rug: ${includeRug ? 'MUST include a rug (randomly chosen: cowhide, jute, chunky wool, flat-weave, etc.) on the floor.' : 'MUST NOT include any rug on the floor. The floor should be bare.'}
- Plants: ${includePlants ? 'MUST include at least one plant, naturally placed (e.g., near window, corner, side table).' : 'MUST NOT include any plants in the scene.'}
- Wall Art: ${includeArt ? 'MUST include wall art (paintings, prints, framed pieces) on at least one wall.' : 'MUST NOT have any wall art, frames, or hanging decor.'}
- Painted Walls: ${includePaintedWalls ? 'The walls MUST be painted in a specific color (not plain white). Describe the wall color explicitly.' : 'Walls MUST remain neutral white or off-white (no colored paint).'}

WINDOW REQUIREMENTS:
- Always at least one window; pick randomly from: Parisian casement, French double, arched, rectangular, or sliding glass panels.
- Do NOT use any skylight, recessed skylight, or ceiling opening of any kind.
- Describe position, shape, and light. Follow WINDOW VIEW RULES.

STRICT FURNITURE & PLANT RULES (must be followed exactly):
- The scene contains exactly ONE sofa. Never add a second sofa.
- The scene may contain up to TWO armchairs (0, 1, or 2 armchairs are allowed). Never add more than 2 armchairs.
- Plants are used tastefully and spaced around the room (e.g., one near a window, one in a corner, maybe a small one on a side table). Never cluster all plants in one spot.

CAMERA & FRAME RULES:
- Prompt must contain "rectilinear ultra-wide lens (18-20mm equivalent)".
- No black borders, vignetting, lens ring, glass, camera body, or any hardware visible — the image must be a clean interior photo.

WINDOW VIEW RULES:
- Outdoor view through windows must be sunny clear‑sky OR warm golden‑hour sunset.
- Choose randomly: city skyline, suburban neighborhood, hillside, or beachside.
- Never overcast, cloudy, rainy, foggy, or gray.

OUTPUT FORMAT (strictly follow this structure, output ONLY valid JSON):
{
  "expandedPrompt": "A detailed, single-paragraph scene generation prompt that starts with 'A vertical 9:16 smartphone photo...' and fully describes the room, furniture, lighting, camera angle, smartphone processing, and the realistic material behavior (matte, micro-roughness, Fresnel). The texture must be described using the exact material description provided above, not just the name. For example: 'the sofa upholstered in a soft bouclé fabric with a matte, nubby texture, zero gloss, and a creamy ivory tone...' The texture description should be woven into the sentence describing the main furniture piece.",

  "guidelines": {
    "composition": "Describe the camera angle, what is visible, and the spatial layout",
    "lighting": "Summarize the lighting setup and how it interacts with the scene",
    "materials": "List the key materials and how the texture is applied with realistic roughness and imperfections",
    "colors": "Describe the color palette and how lighting affects it",
    "details": "Mention any distinctive elements that make the scene feel like a real smartphone photo"
  }
}

RULES:
- The expandedPrompt must be a single flowing paragraph, no line breaks
- Describe the texture application with realistic surface behaviour (matte, micro‑grain, Fresnel)
- The final expandedPrompt MUST be between 1400 and 1600 characters. STRICT LIMIT: 1600 characters. Target 1400-1600 characters. If the prompt exceeds 1700 characters, it will be REJECTED. To stay within limit: use tight, flowing sentences; avoid repeating adjectives; omit filler words; combine descriptions; and skip any detail that is not essential to the visual composition. The prompt must feel atmospheric but never wordy.
- No markdown, no bullet points
- Guidelines should be specific and actionable
- Strictly adhere to the furniture and plant rules above; never add extra sofas or armchairs beyond the limits`;

      // Call Segmind DeepSeek Chat via proxy
      const response = await fetchWithTimeout(`${getSegmindBaseUrl()}/deepseek-chat`, {
        method: 'POST',
        headers: getSegmindAuthHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          model: 'deepseek-chat',
          messages: [{ role: 'user', content: systemPrompt }],
        }),
      }, 120000);

      if (!response.ok) {
        const errText = await response.text();
        throw new Error(`DeepSeek Chat error ${response.status}: ${errText}`);
      }

      const completion = await response.json();
      // Segmind returns OpenAI-compatible format: { choices: [{ message: { content: "..." } }] }
      const rawOutput = completion.choices?.[0]?.message?.content;
      if (!rawOutput) throw new Error('No output received');

      let parsedOutput;
      try {
        const jsonMatch = rawOutput.match(/\{[\s\S]*\}/);
        if (jsonMatch) {
          parsedOutput = JSON.parse(jsonMatch[0]);
        } else {
          throw new Error('No JSON found');
        }
      } catch (parseError) {
        console.warn('Failed to parse JSON, using fallback format');
        parsedOutput = {
          expandedPrompt: rawOutput.trim(),
          guidelines: {
            composition: `Scene described: ${cleanSceneDescription}`,
            lighting: 'See expanded prompt for details',
            materials: 'See expanded prompt for details',
            colors: 'See expanded prompt for details',
            details: 'See expanded prompt for details'
          }
        };
      }

      setSceneExpandedPrompt(parsedOutput.expandedPrompt);
      setSceneGuidelines(parsedOutput.guidelines);
      
      await deductImageCredits(authUser.uid, expandSceneCost, { type: 'scene_expansion' });
      if (updateRemainingCounts) updateRemainingCounts();
      
      setGenerationStatus('✓ Scene composed successfully! Review and edit the prompt, then generate.');
      setTimeout(() => setGenerationStatus(''), 4000);
    } catch (error) {
      console.error('Scene expansion error:', error);
      setGenerationStatus('Scene expansion failed. Please try again.');
      showToast('Failed to expand scene prompt. Please try again.', 'error');
    } finally {
      setIsExpandingScene(false);
    }
  };
  
  // ============================================
  // ASSET STUDIO - GENERATE FLAT CANVAS ART
  // ============================================
  const generateAssetArt = async () => {
    if (!assetPrompt.trim()) {
      showToast('Please describe the artwork first', 'warning');
      return;
    }
    
    if (assetPrompt.length > 5000) {
      showToast('Prompt is too long. Max 5000 characters.', 'warning');
      return;
    }
    
    const model = selectedAssetModel || assetModels[0];
    const cost = typeof model.pricing === 'object' ? model.pricing[model.defaultSize] || 0.15 : model.pricing || 0.035;
    
    setIsGeneratingAsset(true);
    setAssetGenerationStatus(`Generating canvas art with ${model.name}...`);
    
    // Build flat, top-down prompt
    let finalPrompt = `A flat, top-down 2D canvas artwork with no perspective, no depth, no 3D elements. The artwork is perfectly flat and orthographic, suitable for printing on canvas. ${assetPrompt}. The composition is centered and balanced. No shadows, no highlights, no texture, no 3D effects. The background is clean and neutral.`;
    
    const result = await safeGenerate(
      authUser.uid,
      cost,
      async (onProgress) => {
        const body = {
          model: model.endpoint,
          prompt: finalPrompt,
        };
        
        if (model.id === 'nanobanana') {
          body.aspect_ratio = assetAspectRatio;
          body.output_format = 'jpg';
          body.output_resolution = '4K';
        } else if (model.id === 'segmind') {
          body.aspect_ratio = assetAspectRatio;
          body.size = '4K';
          body.max_images = 1;
          body.watermark = false;
          body.optimize_prompt = 'standard';
        }
        
        // Use v2 async for long-running models
        const useAsync = ['nanobanana', 'segmind'].includes(model.id);
        let requestBody;
        let url;
        if (useAsync) {
          requestBody = { model: 'v2-submit', slug: model.endpoint, ...body };
          url = import.meta.env.DEV
            ? `https://api.segmind.com/v2/${model.endpoint}`
            : getSegmindBaseUrl();
        } else {
          requestBody = { model: model.endpoint, ...body };
          url = import.meta.env.DEV
            ? `https://api.segmind.com/v1/${model.endpoint}`
            : getSegmindBaseUrl();
        }
        
        const response = await fetchWithTimeout(url, {
          method: 'POST',
          headers: getSegmindAuthHeaders({ 'Content-Type': 'application/json' }),
          body: JSON.stringify(requestBody),
        }, 120000);
        
        let imageBlob;
        if (useAsync) {
          if (!response.ok) {
            const errText = await response.text();
            throw new Error(`Server error ${response.status}: ${errText}`);
          }
          const data = await response.json();
          if (!data.request_id) throw new Error('No request_id returned from Segmind v2');
          imageBlob = await pollForResult(data.request_id);
        } else {
          const contentType = response.headers.get('content-type') || '';
          if (contentType.includes('application/json')) {
            const json = await response.json();
            const imageUrl = json?.image?.url || json?.output || json?.url;
            if (imageUrl) {
              const imageResponse = await fetch(imageUrl);
              if (!imageResponse.ok) throw new Error(`Failed to fetch image from URL: ${imageResponse.status}`);
              imageBlob = await imageResponse.blob();
            } else {
              throw new Error('Unexpected JSON response');
            }
          } else {
            imageBlob = await response.blob();
          }
        }
        
        const reader = new FileReader();
        const base64Image = await new Promise((resolve, reject) => {
          reader.onloadend = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(imageBlob);
        });
        
        const storage = getStorage();
        const timestamp = Date.now();
        const storagePath = `users/${authUser.uid}/assets/${timestamp}.${model.outputFormat || 'jpg'}`;
        const storageRef = ref(storage, storagePath);
        await uploadString(storageRef, base64Image, 'data_url');
        const permanentUrl = await getDownloadURL(storageRef);
        
        return { assetData: { imageUrl: permanentUrl, storagePath } };
      },
      {
        type: 'image',
        onStart: () => {},
        onProgress: () => {},
        onComplete: (result) => {
          const { assetData } = result;
          setAssetImageUrl(assetData.imageUrl);
          setAssetGenerationStatus('✓ Canvas art generated! Download it below.');
          showToast('Canvas art generated!', 'success');
          setTimeout(() => setAssetGenerationStatus(''), 3000);
        },
        onError: (error) => {
          setAssetGenerationStatus('Generation failed. Please try again.');
          showToast('Failed to generate canvas art.', 'error');
        }
      }
    );
    
    if (result.success) {
      if (updateRemainingCounts) updateRemainingCounts();
    }
    
    setIsGeneratingAsset(false);
  };
  
  // ============================================
  // ASSET STUDIO - DOWNLOAD IMAGE
  // ============================================
  const downloadAssetImage = async () => {
    if (!assetImageUrl) return;
    try {
      // Use fetch to get the image as a blob, then create a download link
      // This prevents the browser from loading the image in a new tab
      const response = await fetch(assetImageUrl);
      if (!response.ok) throw new Error('Failed to fetch image');
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `canvas_art_${Date.now()}.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      showToast('Downloading canvas art...', 'success');
    } catch (error) {
      console.error('Download error:', error);
      showToast('Failed to download image', 'error');
    }
  };

  // ============================================
  // GENERATE FINAL SCENE
  // ============================================
  const generateFinalScene = async () => {
    let finalPrompt = sceneExpandedPrompt;
    
    if (!finalPrompt.trim()) {
      showToast('Please expand your scene description first', 'warning');
      return;
    }
    
    if (finalPrompt.length > 1800) {
      console.warn("Prompt too long, truncating to 1800 chars");
      finalPrompt = finalPrompt.substring(0, 1800);
      const lastPeriod = finalPrompt.lastIndexOf('.');
      if (lastPeriod > 1750) {
        finalPrompt = finalPrompt.substring(0, lastPeriod + 1);
      }
    }
    
    // Force removal of any device UI or status bar
    finalPrompt += ' No phone UI, no status bar, no signal icons, no battery indicator, no screen overlay, no device frame, no camera interface.';
    
    const model = selectedSceneModel;

    // ============================================================
    // KLING‑SPECIFIC PROMPT ENGINEERING (Technical Specification Sheet)
    // ============================================================
    // ============================================================
    // Global Kling‑style realism toggle – apply to all models
    // ============================================================
    const shouldInjectRealism = enableGlobalRealism || model.id === 'kling';

    if (shouldInjectRealism) {
      // Prepend shared positive realism prompt
      finalPrompt = SHARED_POSITIVE_PROMPT + ' ' + finalPrompt;
      // Inject low‑contrast sensor grain
      finalPrompt += KLING_TEXTURE_GRAIN_SUFFIX;

      // For models that don't support a dedicated negative_prompt parameter,
      // append the negative prompt as a soft constraint to the text
      if (model.id !== 'kling') {
        finalPrompt += ' Avoid these: ' + SHARED_NEGATIVE_PROMPT;
      }

      // ============================================================
      // API hard limit: 2500 characters
      // ============================================================
      if (finalPrompt.length > 2500) {
        console.warn('Prompt too long, truncating to 2500 chars');
        finalPrompt = finalPrompt.substring(0, 2500).trim();
        const lastSpace = finalPrompt.lastIndexOf(' ');
        if (lastSpace > 2000) finalPrompt = finalPrompt.substring(0, lastSpace);
      }
    }
    
    const cost = typeof model.pricing === 'object' ? model.pricing[model.defaultSize] || 0.15 : model.pricing || 0.035;
    
    setIsGeneratingScene(true);
    setGenerationStatus(`Generating final scene with ${model.name}...`);
    
    const result = await safeGenerate(
      authUser.uid,
      cost,
      async (onProgress) => {
        const body = {
          model: model.endpoint,
          prompt: finalPrompt,
        };

        // Add model-specific parameters
        if (model.id === 'recraft') {
          body.size = '1024x1024';
          body.style = 'any';
        } else if (model.id === 'nanobanana') {
          body.aspect_ratio = aspectRatio;
          body.output_format = 'jpg';
          body.output_resolution = imageSize;
        } else if (model.id === 'seedream') {
          body.aspect_ratio = aspectRatio;
          body.size = imageSize; // 2K, 3K, etc.
          body.max_images = 1;
          body.watermark = false;
          body.optimize_prompt = 'standard';
               } else if (model.id === 'kling') {
          body.aspect_ratio = aspectRatio;
          body.resolution = '2K'; // Kling only supports 1K or 2K
          body.output_format = 'png';
          body.style = 'photography';                 // Pulls Kling out of illustration mode (if API supports)
          body.cfg_scale = 4.8;                     // Lowered: kills gloss, keeps prompt adherence
          body.negative_prompt = SHARED_NEGATIVE_PROMPT;
          body.creativity = 0.4;                     // Reduces forced symmetry
          body.num_inference_steps = 28;              // Lowered: stops over‑smoothing plastic waxing
        }

                // Use v2 async for long‑running models
        const useAsync = ['kling', 'recraft', 'nanobanana', 'seedream'].includes(model.id);
        let requestBody;
        let url;
        if (useAsync) {
          requestBody = { model: 'v2-submit', slug: model.endpoint, ...body };
          url = import.meta.env.DEV
            ? `https://api.segmind.com/v2/${model.endpoint}`
            : getSegmindBaseUrl();
        } else {
          requestBody = { model: model.endpoint, ...body };
          url = import.meta.env.DEV
            ? `https://api.segmind.com/v1/${model.endpoint}`
            : getSegmindBaseUrl();
        }

        // Increased timeout – the proxy itself may take a few seconds to submit
        const response = await fetchWithTimeout(url, {
          method: 'POST',
          headers: getSegmindAuthHeaders({ 'Content-Type': 'application/json' }),
          body: JSON.stringify(requestBody),
        }, 120000); // 2 minutes

        let imageBlob;
        if (useAsync) {
          if (!response.ok) {
            const errText = await response.text();
            throw new Error(`Server error ${response.status}: ${errText}`);
          }
          const data = await response.json();
          if (!data.request_id) throw new Error('No request_id returned from Segmind v2');
          imageBlob = await pollForResult(data.request_id);
        } else {
          const contentType = response.headers.get('content-type') || '';
          if (contentType.includes('application/json')) {
            const json = await response.json();
            const imageUrl = json?.image?.url || json?.output || json?.url;
            if (imageUrl) {
              const imageResponse = await fetch(imageUrl);
              if (!imageResponse.ok) throw new Error(`Failed to fetch scene image from URL: ${imageResponse.status}`);
              imageBlob = await imageResponse.blob();
            } else {
              throw new Error('Unexpected JSON response');
            }
          } else {
            imageBlob = await response.blob();
          }
        }

        const reader = new FileReader();
        const base64Image = await new Promise((resolve, reject) => {
          reader.onloadend = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(imageBlob);
        });

        const storage = getStorage();
        const timestamp = Date.now();
        const storagePath = `users/${authUser.uid}/marketing-images/${timestamp}.${model.outputFormat || 'jpg'}`;
        const storageRef = ref(storage, storagePath);
        await uploadString(storageRef, base64Image, 'data_url');
        const permanentUrl = await getDownloadURL(storageRef);

        const sceneData = {
          id: timestamp.toString(),
          userId: authUser.uid,
          prompt: finalPrompt,
          shortSceneDescription: sceneShortPrompt,
          sceneGuidelines: sceneGuidelines,
          texturePrompt: textureExpandedPrompt,
          textureShortDescription: textureShortPrompt,
          textureGuidelines: textureGuidelines,
          textureId: generatedTexture?.id || null,
          textureImageUrl: generatedTexture?.imageUrl || null,
          model: model.name,
          modelId: model.id,
          imageUrl: permanentUrl,
          storagePath: storagePath,
          resolution: imageSize,
          aspectRatio: aspectRatio,
          workflowType: 'texture-to-scene',
          type: 'scene',
          createdAt: serverTimestamp(),
        };

        return { sceneData };
      },
      {
        type: 'image',
        onStart: () => {},
        onProgress: () => {},
        onComplete: (result) => {
          const { sceneData } = result;
          setGeneratedScene(sceneData);
          setCurrentStep('result');
          setGenerationStatus('✓ Scene generated successfully!');
          showToast('Scene generated successfully!', 'success');
          setTimeout(() => setGenerationStatus(''), 3000);
        },
        onError: (error) => {
          if (error.message?.includes('timed out')) {
            setGenerationStatus('Generation took too long. Try a shorter prompt or different model.');
            showToast('Generation timed out. Try reducing the prompt length.', 'error');
          } else if (error.message?.includes('download')) {
            setGenerationStatus('Image generated but download failed. Please try again.');
            showToast('Image was generated but could not be downloaded. Please try again.', 'error');
          } else {
            setGenerationStatus('Scene generation failed. Please try again.');
            showToast('Failed to generate scene. Please try again.', 'error');
          }
        }
      }
    );
    
    if (result.success) {
      // Validate scene data before saving
      if (result.result.sceneData && result.result.sceneData.imageUrl && result.result.sceneData.imageUrl.startsWith('http')) {
        const imagesRef = collection(db, 'marketing_images');
        await addDoc(imagesRef, result.result.sceneData);
      } else {
        console.error('Invalid scene data - not saving to database:', result.result.sceneData);
        showToast('Generated scene is invalid and will not be saved. Please try again.', 'error');
      }
      if (updateRemainingCounts) updateRemainingCounts();
    }
    
    setIsGeneratingScene(false);
  };
  
  // ============================================
  // DOWNLOAD IMAGE
  // ============================================
  const downloadImage = async (imageUrl, imageId) => {
    if (isDownloading) return;
    setIsDownloading(true);
    try {
      // Use fetch to get the image as a blob, then create a download link
      // This prevents the browser from loading the image in a new tab
      const response = await fetch(imageUrl);
      if (!response.ok) throw new Error('Failed to fetch image');
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `image_${imageId}.${selectedSceneModel.outputFormat || 'jpg'}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download error:', error);
      showToast('Failed to download image', 'error');
    } finally {
      setIsDownloading(false);
    }
  };

  // ============================================
  // UPSCALE IMAGE (Topaz AI) – works on any image object
  // ============================================
  const upscaleImage = async (imageObj) => {
    if (isUpscaling) return;
    if (!imageObj?.imageUrl) {
      showToast('Invalid image', 'error');
      return;
    }
    setIsUpscaling(true);
    setGenerationStatus('Upscaling image with Topaz AI…');

    try {
      const response = await fetchWithTimeout(`${getSegmindBaseUrl()}/topaz-image-upscale`, {
        method: 'POST',
        headers: getSegmindAuthHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          model: 'topaz-image-upscale',
          image: imageObj.imageUrl,
          enhance_model: 'Standard V2',
          output_format: 'jpg',
          upscale_factor: '2x',
          face_enhancement: false,
          subject_detection: 'None',
          face_enhancement_strength: 0.8,
          face_enhancement_creativity: 0,
        }),
      });

      if (!response.ok) {
        const errText = await response.text();
        throw new Error(`Upscale error ${response.status}: ${errText}`);
      }

      const blob = await response.blob();
      const reader = new FileReader();
      const base64Image = await new Promise((resolve, reject) => {
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });

      const storage = getStorage();
      const timestamp = Date.now();
      const storagePath = `users/${authUser.uid}/upscaled/${timestamp}.jpg`;
      const storageRef = ref(storage, storagePath);
      await uploadString(storageRef, base64Image, 'data_url');
      const permanentUrl = await getDownloadURL(storageRef);

      // Update Firestore document if we have a valid image id
      if (imageObj.id) {
        const imageRef = doc(db, 'marketing_images', imageObj.id);
        await updateDoc(imageRef, {
          imageUrl: permanentUrl,
          storagePath,
          resolution: '4K',
        });
      }

      // Update local gallery state
      setGeneratedImages((prev) =>
        prev.map((img) =>
          img.id === imageObj.id
            ? { ...img, imageUrl: permanentUrl, storagePath, resolution: '4K' }
            : img
        )
      );

      // If this is the currently viewed generated scene, update that state too
      if (generatedScene?.id === imageObj.id) {
        setGeneratedScene((prev) => ({
          ...prev,
          imageUrl: permanentUrl,
          storagePath,
          resolution: '4K',
        }));
      }

      setGenerationStatus('✓ Image upscaled to 4K!');
      showToast('Upscale complete!', 'success');
      setTimeout(() => setGenerationStatus(''), 3000);
    } catch (error) {
      console.error('Upscale error:', error);
      setGenerationStatus('Upscale failed. Please try again.');
      showToast('Failed to upscale image.', 'error');
    } finally {
      setIsUpscaling(false);
    }
  };

  // ============================================
  // DELETE IMAGE
  // ============================================
  const deleteImage = async (image, isSilent = false) => {
  if (!image?.id) {
    console.error('Cannot delete: Image ID is missing');
    if (!isSilent) showToast('Cannot delete this image - ID is missing', 'error');
    return;
  }
  
  // For manual deletion, show confirmation toast with image name
  if (!isSilent) {
    const imageName = image.shortDescription || image.shortSceneDescription || image.prompt?.substring(0, 50) || 'this image';
    showConfirm(`Delete "${imageName}"? This action cannot be undone.`, () => {
      performDelete(image, isSilent);
    });
    return;
  }
  
  // For silent auto-delete, proceed immediately
  await performDelete(image, isSilent);
};
  
  const performDelete = async (image, isSilent) => {
    let storageDeleted = false;
    let firestoreDeleted = false;
    
    try {
      // Step 1: Try to delete from Firebase Storage first
      if (image.storagePath) {
        try {
          const storage = getStorage();
          const imageStorageRef = ref(storage, image.storagePath);
          await deleteObject(imageStorageRef);
          storageDeleted = true;
          console.log('Storage file deleted successfully');
        } catch (storageError) {
          if (storageError.code === 'storage/object-not-found') {
            console.log('Storage file already deleted');
            storageDeleted = true; // Consider it done if already deleted
          } else {
            console.warn('Storage delete error:', storageError.message);
          }
        }
      }
      
      // Step 2: Try to delete from Firestore
      try {
        const imageRef = doc(db, 'marketing_images', image.id);
        await deleteDoc(imageRef);
        firestoreDeleted = true;
        console.log('Firestore document deleted successfully');
      } catch (firestoreError) {
        if (firestoreError.code === 'not-found') {
          console.log('Firestore document already deleted');
          firestoreDeleted = true;
        } else {
          console.error('Firestore delete error:', firestoreError);
        }
      }
      
      // Step 3: Update UI regardless of success
      setGeneratedImages(prev => prev.filter(img => img.id !== image.id));
      
      if (selectedImage?.id === image.id) {
        setSelectedImage(null);
        setShowImageDetails(false);
      }
      
      // Step 4: Show appropriate message
      if (!isSilent) {
        if (storageDeleted && firestoreDeleted) {
          showToast('Image deleted successfully', 'success');
        } else if (storageDeleted || firestoreDeleted) {
          showToast('Image partially deleted. It may still appear in some places.', 'warning');
        } else {
          showToast('Failed to delete image completely. Please try again.', 'error');
        }
      }
      
    } catch (error) {
      console.error('Unexpected delete error:', error);
      
      // Still remove from UI to prevent broken state
      setGeneratedImages(prev => prev.filter(img => img.id !== image.id));
      if (selectedImage?.id === image.id) {
        setSelectedImage(null);
        setShowImageDetails(false);
      }
      
      if (!isSilent) {
        showToast('Error during deletion. Image removed from view but may still exist.', 'error');
      }
    }
  };

  
  // ============================================
  // COPY TO CLIPBOARD
  // ============================================
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setGenerationStatus('✓ Copied to clipboard!');
    showToast('Copied to clipboard!', 'success', 1500);
    setTimeout(() => setGenerationStatus(''), 2000);
  };

    

  
  // ============================================
  // RESET WORKFLOW
  // ============================================
  const resetWorkflow = () => {
    setTextureShortPrompt('');
    setTextureExpandedPrompt('');
    setTextureGuidelines(null);
    setGeneratedTexture(null);
    setSceneShortPrompt('');
    setSceneExpandedPrompt('');
    setSceneGuidelines(null);
    setGeneratedScene(null);
    setCurrentStep('texture');
    setGenerationStatus('');
  };
  
  // ============================================
  // MEMOIZED FILTERED GALLERY IMAGES
  // ============================================
  const filteredImages = React.useMemo(() => {
    if (galleryFilter === 'textures') {
      return generatedImages.filter(img => img.type === 'texture');
    } else if (galleryFilter === 'scenes') {
      return generatedImages.filter(img => img.type === 'scene' || img.workflowType === 'texture-to-scene');
    }
    return generatedImages;
  }, [generatedImages, galleryFilter]);

  // ============================================
  // MEMOIZED GALLERY HANDLERS
  // ============================================
  const handleSelectImage = useCallback((image) => {
    setSelectedImage(image);
    setShowImageDetails(true);
  }, []);

  const handleDownloadImage = useCallback((imageUrl, imageId) => {
    downloadImage(imageUrl, imageId);
  }, [downloadImage]);

  const handleDeleteImage = useCallback((image) => {
    deleteImage(image, false);
  }, [deleteImage]);

  const handleReuseImage = useCallback((image) => {
    setGeneratedTexture({
      id: image.textureId,
      shortDescription: image.textureShortDescription,
      prompt: image.texturePrompt,
      imageUrl: image.textureImageUrl,
    });
    setTextureShortPrompt(image.textureShortDescription || '');
    setTextureExpandedPrompt(image.texturePrompt || '');
    setSceneShortPrompt(image.shortSceneDescription || '');
    setSceneExpandedPrompt(image.prompt || '');
    setCurrentStep('scene');
    setGenerationStatus('✓ Previous scene loaded — expand or generate again');
    setTimeout(() => setGenerationStatus(''), 2000);
  }, []);

  const handleUpscaleImage = useCallback((image) => {
    upscaleImage(image);
  }, [upscaleImage]);

  // ============================================
  // BULK SELECTION HANDLERS
  // ============================================
  const toggleSelectImage = useCallback((imageId) => {
    setSelectedImageIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(imageId)) {
        newSet.delete(imageId);
      } else {
        newSet.add(imageId);
      }
      return newSet;
    });
  }, []);

  const selectAllImages = useCallback(() => {
    const visibleImageIds = filteredImages.map(img => img.id);
    const allSelected = visibleImageIds.every(id => selectedImageIds.has(id));
    
    if (allSelected) {
      setSelectedImageIds(new Set());
    } else {
      setSelectedImageIds(new Set(visibleImageIds));
    }
  }, [filteredImages, selectedImageIds]);

  // ============================================
  // BULK DOWNLOAD HANDLER
  // ============================================
  const bulkDownload = useCallback(async () => {
    const selectedImages = filteredImages.filter(img => selectedImageIds.has(img.id));
    if (selectedImages.length === 0) {
      showToast('No images selected', 'warning');
      return;
    }

    setIsBulkDownloading(true);
    setGenerationStatus(`Preparing ${selectedImages.length} images for download...`);

    try {
      if (selectedImages.length === 1) {
        // Single image: just download directly
        const img = selectedImages[0];
        await downloadImage(img.imageUrl, img.id);
        setGenerationStatus(`✓ Downloaded ${img.id}`);
        setTimeout(() => setGenerationStatus(''), 2000);
        setIsBulkDownloading(false);
        return;
      }

      // Multiple images: create ZIP
      // Dynamically import JSZip
      const JSZip = (await import('jszip')).default;
      const zip = new JSZip();
      
      let downloadedCount = 0;
      const totalImages = selectedImages.length;
      
      for (const img of selectedImages) {
        try {
          const response = await fetch(img.imageUrl);
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
          const blob = await response.blob();
          const extension = img.model?.includes('seedream') ? 'jpg' : 
                           img.model?.includes('recraft') ? 'webp' : 'png';
          const filename = `${img.type || 'image'}_${img.id}.${extension}`;
          zip.file(filename, blob);
          downloadedCount++;
          setGenerationStatus(`Downloading ${downloadedCount}/${totalImages}...`);
        } catch (err) {
          console.error('Failed to download image:', img.id, err);
        }
      }

      if (downloadedCount === 0) {
        throw new Error('No images could be downloaded');
      }

      // Generate ZIP file
      const zipBlob = await zip.generateAsync({ 
        type: 'blob',
        compression: 'DEFLATE',
        compressionOptions: { level: 6 }
      });
      
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
      const zipFilename = `gallery_export_${timestamp}.zip`;
      saveAs(zipBlob, zipFilename);
      
      setGenerationStatus(`✓ Downloaded ${downloadedCount} images as ZIP`);
      showToast(`Downloaded ${downloadedCount} images as ZIP`, 'success');
      
      // Clear selection after download
      setSelectedImageIds(new Set());
      setShowBulkSelect(false);
      
      setTimeout(() => setGenerationStatus(''), 3000);
    } catch (error) {
      console.error('Bulk download error:', error);
      showToast('Failed to create ZIP file. Please try again.', 'error');
      setGenerationStatus('Bulk download failed');
    } finally {
      setIsBulkDownloading(false);
    }
  }, [filteredImages, selectedImageIds, downloadImage, showToast]);

  // ============================================
  // RENDER GUIDELINES PANEL
  // ============================================
  const renderGuidelinesPanel = (guidelines, type = 'texture') => {
    if (!guidelines) return null;
    
    return (
      <div style={{
        background: 'white',
        borderRadius: '12px',
        border: '1px solid #e0e0e0',
        overflow: 'hidden',
        marginTop: '16px',
      }}>
        <div style={{
          padding: '12px 16px',
          background: 'linear-gradient(135deg, #88b7ff 0%, #6ba6ff 100%)',
          color: 'white',
          fontFamily: 'Avenir, sans-serif',
          fontWeight: 600,
          fontSize: '0.85rem',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
        }}>
          <FiInfo size={16} />
          AI Guidelines for {type === 'texture' ? 'Texture' : 'Scene'}
        </div>
        <div style={{ padding: '16px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}>
          {Object.entries(guidelines).map(([key, value]) => (
            <div key={key} style={{
              padding: '12px',
              background: '#f9f9f9',
              borderRadius: '8px',
              border: '1px solid #eee',
            }}>
              <div style={{
                fontSize: '0.65rem',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                color: '#88b7ff',
                fontFamily: 'Avenir, sans-serif',
                fontWeight: 600,
                marginBottom: '6px',
              }}>
                {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
              </div>
              <div style={{
                fontSize: '0.8rem',
                color: '#333',
                fontFamily: 'Avenir, sans-serif',
                lineHeight: '1.5',
              }}>
                {value}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };
  
  // ============================================
  // RENDER COMPONENT
  // ============================================
  return (
    <>
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        
        @keyframes slideUp {
          from { transform: translateY(10px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
        
        .animate-slide-up {
          animation: slideUp 0.3s ease-out;
        }
        
        .step-indicator {
          width: 32px;
          height: 32px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-family: 'Avenir', sans-serif;
          font-weight: 600;
          font-size: 0.85rem;
          transition: all 0.3s ease;
        }
        
        .step-indicator.active {
          background: #88b7ff;
          color: white;
          box-shadow: 0 2px 8px rgba(136, 183, 255, 0.4);
        }
        
        .step-indicator.completed {
          background: #4caf50;
          color: white;
        }
        
        .step-indicator.inactive {
          background: #e0e0e0;
          color: #999;
        }
        
        .workflow-card {
          background: white;
          border-radius: 16px;
          border: 1px solid #eaeaea;
          padding: 24px;
          margin-bottom: 16px;
          transition: all 0.3s ease;
        }
        
        .workflow-card:hover {
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
        }
        
        .model-dropdown {
          position: absolute;
          top: 100%;
          left: 0;
          right: 0;
          background: white;
          border: 1px solid #eaeaea;
          border-radius: 12px;
          margin-top: 4px;
          z-index: 100;
          box-shadow: 0 4px 12px rgba(0,0,0,0.1);
          overflow: hidden;
        }
        
        .model-option {
          padding: 12px 16px;
          cursor: pointer;
          transition: background 0.2s ease;
          border-bottom: 1px solid #f0f0f0;
        }
        
        .model-option:last-child { border-bottom: none; }
        .model-option:hover { background: #f5f5f5; }
        .model-option.selected { background: #e8f0ff; }
        
        /* Masonry gallery */
        .gallery-masonry {
          column-count: 4;
          column-gap: 8px;
        }
        @media (max-width: 1024px) {
          .gallery-masonry { column-count: 3; }
        }
        @media (max-width: 768px) {
          .gallery-masonry { column-count: 2; column-gap: 6px; }
        }
        .gallery-item-masonry {
          break-inside: avoid;
          margin-bottom: 8px;
          border-radius: 12px;
          overflow: hidden;
          cursor: pointer;
          transition: all 0.2s ease;
          border: 1px solid #eaeaea;
          background: white;
          position: relative;
        }
        .gallery-item-masonry:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        }
        .gallery-item-masonry img {
          width: 100%;
          display: block;
        }
        
        .filter-bar {
          display: inline-flex;
          background: #f0f0f0;
          border-radius: 20px;
          padding: 3px;
          gap: 2px;
        }
        
        .filter-chip {
          padding: 6px 16px;
          border-radius: 18px;
          cursor: pointer;
          transition: all 0.2s ease;
          font-family: 'Avenir', sans-serif;
          font-size: 0.75rem;
          font-weight: 500;
          color: #666;
          background: transparent;
          border: none;
          white-space: nowrap;
        }
        
        .filter-chip.active {
          background: white;
          color: #88b7ff;
          box-shadow: 0 1px 3px rgba(0,0,0,0.1);
        }
        
        .input-field {
          width: 100%;
          padding: 12px;
          border-radius: 12px;
          border: 1px solid #ddd;
          font-family: 'Avenir', sans-serif;
          font-size: 0.85rem;
          transition: all 0.2s;
          box-sizing: border-box;
          background: #fafafa;
        }
        
        .input-field:focus {
          outline: none;
          border-color: #88b7ff;
          background: white;
          box-shadow: 0 0 0 3px rgba(136, 183, 255, 0.1);
        }
        
        textarea.input-field {
          resize: vertical;
          font-family: 'Roboto Mono', sans-serif;
          font-size: 0.75rem;
          line-height: 1.6;
        }
        
        .btn-primary {
          padding: 12px 24px;
          border-radius: 30px;
          border: none;
          background: linear-gradient(135deg, #88b7ff 0%, #6ba6ff 100%);
          color: white;
          cursor: pointer;
          font-family: 'Avenir', sans-serif;
          font-weight: 600;
          font-size: 0.9rem;
          transition: all 0.2s;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
        }
        
        .btn-primary:hover:not(:disabled) {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(136, 183, 255, 0.3);
        }
        
        .btn-primary:disabled {
          background: #e0e0e0;
          color: #999;
          cursor: not-allowed;
        }
        
        .btn-secondary {
          padding: 10px 20px;
          border-radius: 30px;
          border: 1px solid #88b7ff;
          background: white;
          color: #88b7ff;
          cursor: pointer;
          font-family: 'Avenir', sans-serif;
          font-size: 0.85rem;
          transition: all 0.2s;
          display: flex;
          align-items: center;
          gap: 8px;
        }
        
        .btn-secondary:hover:not(:disabled) {
          background: #f0f4ff;
        }
        
        @media (max-width: 767px) {
          .dev-studio-overlay { padding: 0 !important; background-color: #fafafa !important; }
          .dev-studio-container { width: 100% !important; height: 100vh !important; border-radius: 0 !important; }
          .workflow-card { padding: 16px !important; }
          .gallery-masonry { column-count: 2 !important; column-gap: 6px !important; }
        }
        
        @media (min-width: 768px) {
          .dev-studio-overlay { background: rgba(0, 0, 0, 0.5) !important; }
          .dev-studio-container { max-width: 1200px !important; border-radius: 24px !important; }
        }
        
        /* Details panel side-by-side - removed since we now use modal */
        .gallery-details-layout {
          display: block;
        }
      `}</style>
      
      <div 
        className="dev-studio-overlay"
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 100,
          backgroundColor: isMobile ? '#FAFAFA' : 'rgba(0, 0, 0, 0.5)',
          backdropFilter: isMobile ? 'none' : 'blur(4px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: isMobile ? '0' : '20px',
        }} 
        onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      >
        <div 
          className="dev-studio-container"
          style={{
            backgroundColor: '#fafafa',
            width: '100%',
            maxWidth: isMobile ? '100%' : '1200px',
            height: isMobile ? '100%' : '90vh',
            maxHeight: isMobile ? '100%' : 'calc(100vh - 40px)',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            boxShadow: isMobile ? 'none' : '0 20px 40px rgba(0, 0, 0, 0.2)',
          }}
        >
          {/* HEADER */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexShrink: 0,
            padding: '16px 24px',
            borderBottom: '1px solid #eaeaea',
            background: 'white',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <button onClick={onClose} style={{
                background: '#d9d9d9',
                border: 'none',
                padding: '6px 20px',
                borderRadius: '20px',
                cursor: 'pointer',
                fontFamily: 'Avenir, sans-serif',
                fontSize: '0.8rem',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                color: '#fff',
              }}>
                <FiArrowLeft size={14} /> Back
              </button>
              <h2 style={{
                fontFamily: 'Avenir, sans-serif',
                fontWeight: 600,
                fontSize: isMobile ? '1.1rem' : '1.4rem',
                color: '#333',
                margin: 0,
              }}>
                {currentStep === 'asset' ? 'Asset Studio' : 'Texture-to-Scene Studio'}
              </h2>
            </div>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <div style={{
                background: '#f0f4ff',
                borderRadius: '20px',
                padding: '6px 14px',
                color: '#88b7ff',
                fontSize: '0.75rem',
                fontFamily: 'Avenir, sans-serif',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
              }}>
                <FiZap size={14} />
                {imagesRemaining} credits
              </div>
              <button
                onClick={() => {
                  // Toggle between Texture-to-Scene and Asset Studio
                  if (currentStep === 'asset') {
                    setCurrentStep('texture');
                  } else {
                    setCurrentStep('asset');
                  }
                }}
                style={{
                  background: currentStep === 'asset' ? '#6ba6ff' : '#88b7ff',
                  border: 'none',
                  padding: '6px 14px',
                  borderRadius: '20px',
                  cursor: 'pointer',
                  color: 'white',
                  fontSize: '0.7rem',
                  fontFamily: 'Avenir, sans-serif',
                  fontWeight: 500,
                }}
              >
                {currentStep === 'asset' ? '← Texture Studio' : 'Asset Studio'}
              </button>
            </div>
          </div>
          
          {/* PROGRESS STEPS */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px 24px',
            gap: '0px',
            background: 'white',
            borderBottom: '1px solid #eaeaea',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div className={`step-indicator ${currentStep === 'texture' ? 'active' : (currentStep === 'scene' || currentStep === 'result' ? 'completed' : 'inactive')}`}>
                {currentStep === 'scene' || currentStep === 'result' ? '✓' : '1'}
              </div>
              <span style={{ fontFamily: 'Avenir, sans-serif', fontSize: '0.8rem', color: currentStep === 'texture' ? '#333' : '#999' }}>
                Texture
              </span>
            </div>
            
            <div style={{ width: '60px', height: '2px', background: currentStep === 'scene' || currentStep === 'result' ? '#4caf50' : '#e0e0e0', margin: '0 16px' }} />
            
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div className={`step-indicator ${currentStep === 'scene' ? 'active' : (currentStep === 'result' ? 'completed' : 'inactive')}`}>
                {currentStep === 'result' ? '✓' : '2'}
              </div>
              <span style={{ fontFamily: 'Avenir, sans-serif', fontSize: '0.8rem', color: currentStep === 'scene' ? '#333' : '#999' }}>
                Scene
              </span>
            </div>
            
            <div style={{ width: '60px', height: '2px', background: currentStep === 'result' ? '#4caf50' : '#e0e0e0', margin: '0 16px' }} />
            
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div className={`step-indicator ${currentStep === 'result' ? 'active' : 'inactive'}`}>
                3
              </div>
              <span style={{ fontFamily: 'Avenir, sans-serif', fontSize: '0.8rem', color: currentStep === 'result' ? '#333' : '#999' }}>
                Result
              </span>
            </div>
          </div>
          
          {/* MAIN CONTENT */}
          <div style={{
            flex: 1,
            overflowY: 'auto',
            padding: '24px',
            display: 'flex',
            flexDirection: 'column',
            gap: '20px',
          }}>
            
                       {/* STEP 1: TEXTURE GENERATION */}
            {currentStep === 'texture' && (
              <div className="workflow-card animate-slide-up">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                  <h3 style={{
                    fontFamily: 'Avenir, sans-serif',
                    fontWeight: 600,
                    fontSize: '1.1rem',
                    color: '#333',
                    margin: 0,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                  }}>
                    <span style={{
                      background: '#88b7ff',
                      color: 'white',
                      width: '24px',
                      height: '24px',
                      borderRadius: '50%',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '0.8rem',
                    }}>1</span>
                    Generate Texture
                  </h3>
                  
                  {generatedTexture && (
                    <button
                      onClick={() => setCurrentStep('scene')}
                      className="btn-primary"
                      style={{ padding: '8px 16px', fontSize: '0.8rem' }}
                    >
                      Continue to Scene <FiChevronRight size={16} />
                    </button>
                  )}
                </div>
                
                <div style={{ marginBottom: '16px' }}>
                  <label style={{
                    fontFamily: 'Avenir, sans-serif',
                    fontSize: '0.8rem',
                    color: '#666',
                    display: 'block',
                    marginBottom: '8px',
                    fontWeight: 500,
                  }}>
                    What texture do you want to create?
                  </label>
                  <textarea
                    value={textureShortPrompt}
                    onChange={(e) => setTextureShortPrompt(e.target.value)}
                    placeholder='Describe your texture briefly... (e.g., "Carrara white marble with dramatic gray veining")'
                    rows={2}
                    className="input-field"
                    style={{ background: 'white' }}
                  />
                  <div style={{ fontSize: '0.7rem', color: '#999', marginTop: '6px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <FiInfo size={12} /> Be specific about colors, patterns, and materials for best results
                  </div>
                </div>
                
                <div style={{ marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontFamily: 'Avenir, sans-serif', fontSize: '0.75rem', color: includeTechnique ? '#88b7ff' : '#666' }}>
                    <input
                      type="checkbox"
                      checked={includeTechnique}
                      onChange={(e) => setIncludeTechnique(e.target.checked)}
                      style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                    />
                    Include a wall‑finish technique
                  </label>
                </div>
                
                <div style={{ display: 'flex', gap: '12px', marginBottom: '16px', flexWrap: 'wrap' }}>
                  <button
                    onClick={expandTexturePrompt}
                    disabled={isExpandingTexture || !textureShortPrompt.trim()}
                    className="btn-secondary"
                  >
                    {isExpandingTexture ? (
                      <>
                        <div style={{ width: '14px', height: '14px', border: '2px solid #88b7ff', borderTop: '2px solid transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                        Analyzing...
                      </>
                    ) : (
                      <>
                        <FiZap size={14} /> Expand with AI
                      </>
                    )}
                  </button>
                  
                  <button
                    onClick={generateRandomTexture}
                    disabled={isExpandingTexture}
                    className="btn-secondary"
                  >
                    <FiRefreshCcw size={14} /> Randomize
                  </button>
                  <button
                    onClick={() => setIsTextureSelectionMode(true)}
                    className="btn-secondary"
                  >
                    <FiImage size={14} /> Use from Gallery
                  </button>
                  
                  {textureExpandedPrompt && (
                    <button
                      onClick={() => copyToClipboard(textureExpandedPrompt)}
                      className="btn-secondary"
                      style={{ borderColor: '#ccc', color: '#666' }}
                    >
                      <FiCopy size={14} /> Copy Prompt
                    </button>
                  )}
                </div>
                
                {textureExpandedPrompt && (
                  <div style={{ marginBottom: '16px' }} className="animate-slide-up">
                    <div style={{ display: 'flex', gap: '4px', marginBottom: '8px', background: '#f0f0f0', borderRadius: '8px', padding: '4px', width: 'fit-content' }}>
                      <button
                        onClick={() => setActiveGuidelineTab('prompt')}
                        style={{
                          padding: '6px 14px',
                          borderRadius: '6px',
                          border: 'none',
                          background: activeGuidelineTab === 'prompt' ? 'white' : 'transparent',
                          color: activeGuidelineTab === 'prompt' ? '#333' : '#666',
                          cursor: 'pointer',
                          fontFamily: 'Avenir, sans-serif',
                          fontSize: '0.75rem',
                          fontWeight: activeGuidelineTab === 'prompt' ? 600 : 400,
                        }}
                      >
                        Full Prompt
                      </button>
                      <button
                        onClick={() => setActiveGuidelineTab('guidelines')}
                        style={{
                          padding: '6px 14px',
                          borderRadius: '6px',
                          border: 'none',
                          background: activeGuidelineTab === 'guidelines' ? 'white' : 'transparent',
                          color: activeGuidelineTab === 'guidelines' ? '#333' : '#666',
                          cursor: 'pointer',
                          fontFamily: 'Avenir, sans-serif',
                          fontSize: '0.75rem',
                          fontWeight: activeGuidelineTab === 'guidelines' ? 600 : 400,
                        }}
                      >
                        AI Guidelines
                      </button>
                    </div>
                    
                    {activeGuidelineTab === 'prompt' ? (
                      <div>
                        <textarea
                          value={textureExpandedPrompt}
                          onChange={(e) => setTextureExpandedPrompt(e.target.value)}
                          rows={5}
                          className="input-field"
                          style={{ fontFamily: 'Roboto Mono, sans-serif', fontSize: '0.7rem', background: 'white' }}
                        />
                        <div style={{ fontSize: '0.7rem', color: textureExpandedPrompt.length >= 1800 ? '#e02f1d' : '#999', marginTop: '4px' }}>
                          {textureExpandedPrompt.length}/2000 characters {textureExpandedPrompt.length > 2000 && '⚠️ Too long'}
                        </div>
                      </div>
                    ) : (
                      renderGuidelinesPanel(textureGuidelines, 'texture')
                    )}
                  </div>
                )}
                
                <div style={{ marginBottom: '16px', position: 'relative' }} ref={modelDropdownRef}>
                  <label style={{
                    fontFamily: 'Avenir, sans-serif',
                    fontSize: '0.8rem',
                    color: '#666',
                    display: 'block',
                    marginBottom: '8px',
                    fontWeight: 500,
                  }}>
                    AI Model
                  </label>
                  <button
                    onClick={() => setShowModelDropdown(!showModelDropdown)}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      background: 'white',
                      border: '1px solid #ddd',
                      borderRadius: '12px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      cursor: 'pointer',
                      fontFamily: 'Avenir, sans-serif',
                      fontSize: '0.85rem',
                      color: '#333',
                    }}
                  >
                    <span>{selectedTextureModel.name}</span>
                    <FiChevronDown size={16} style={{ transform: showModelDropdown ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
                  </button>
                  
                  {showModelDropdown && (
                    <div className="model-dropdown" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                      {models.map((model) => (
                        <div
                          key={model.id}
                          className={`model-option ${selectedTextureModel.id === model.id ? 'selected' : ''}`}
                          onClick={() => { setSelectedTextureModel(model); setShowModelDropdown(false); }}
                        >
                          <div style={{ fontWeight: selectedTextureModel.id === model.id ? 600 : 400 }}>{model.name}</div>
                          <div style={{ fontSize: '0.7rem', color: '#888', marginTop: '2px' }}>{model.description}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                
                <button
                  onClick={generateTextureImage}
                  disabled={isGeneratingTexture || !textureExpandedPrompt.trim() || textureExpandedPrompt.length > 2000}
                  className="btn-primary"
                  style={{ width: '100%', padding: '14px' }}
                >
                  {isGeneratingTexture ? (
                    <>
                      <div style={{ width: '18px', height: '18px', border: '2px solid white', borderTop: '2px solid transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                      Generating Texture...
                    </>
                  ) : (
                    <><FiImage size={16} /> Generate Texture</>
                  )}
                </button>
                
                {showTextureSuccess && generatedTexture && (
                  <div style={{
                    marginTop: '16px',
                    padding: '16px',
                    background: '#e8f5e9',
                    borderRadius: '12px',
                    border: '1px solid #4caf50',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                  }} className="animate-slide-up">
                    <FiCheckCircle size={24} color="#4caf50" />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontFamily: 'Avenir, sans-serif', fontWeight: 600, color: '#2e7d32', fontSize: '0.9rem' }}>
                        Texture Ready!
                      </div>
                      <div style={{ fontFamily: 'Avenir, sans-serif', fontSize: '0.75rem', color: '#555' }}>
                        "{textureShortPrompt}" has been created and linked to your scene
                      </div>
                    </div>
                    <div style={{ width: '60px', height: '60px', borderRadius: '8px', overflow: 'hidden', border: '2px solid #4caf50' }}>
                      <img src={generatedTexture.imageUrl} alt="Generated texture" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    </div>
                  </div>
                )}
                
                {generationStatus && (
                  <div style={{ marginTop: '12px', fontFamily: 'Avenir, sans-serif', fontSize: '0.8rem', color: '#88b7ff', textAlign: 'center', padding: '8px', background: '#f0f4ff', borderRadius: '8px' }}>
                    {generationStatus}
                  </div>
                )}
              </div>
            )}
            
            {/* ASSET STUDIO - FLAT CANVAS ART GENERATION */}
            {currentStep === 'asset' && (
              <div className="workflow-card animate-slide-up">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                  <h3 style={{
                    fontFamily: 'Avenir, sans-serif',
                    fontWeight: 600,
                    fontSize: '1.1rem',
                    color: '#333',
                    margin: 0,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                  }}>
                    <span style={{
                      background: '#6ba6ff',
                      color: 'white',
                      width: '24px',
                      height: '24px',
                      borderRadius: '50%',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '0.8rem',
                    }}>
                      <FiImage size={14} />
                    </span>
                    Flat Canvas Art
                  </h3>
                  
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <span style={{
                    fontSize: '0.7rem',
                    color: '#999',
                    fontFamily: 'Avenir, sans-serif',
                  }}>
                    {assetPrompt.length}/5000
                  </span>
                    {assetImageUrl && (
                      <button
                        onClick={() => setAssetImageUrl(null)}
                        className="btn-secondary"
                        style={{ padding: '4px 12px', fontSize: '0.7rem' }}
                      >
                        <FiX size={12} /> Clear
                      </button>
                    )}
                  </div>
                </div>
                
                {/* Prompt Input */}
                <div style={{ marginBottom: '16px' }}>
                  <label style={{
                    fontFamily: 'Avenir, sans-serif',
                    fontSize: '0.8rem',
                    color: '#666',
                    display: 'block',
                    marginBottom: '8px',
                    fontWeight: 500,
                  }}>
                    Describe the canvas artwork
                  </label>
                  <textarea
                    value={assetPrompt}
                    onChange={(e) => setAssetPrompt(e.target.value)}
                    placeholder='Describe a flat, top-down canvas art piece. Examples: "Abstract geometric shapes in muted earth tones", "Minimalist botanical line art on cream background", "Watercolor splatter in warm pastels"'
                    rows={8}
                    className="input-field"
                    style={{ background: 'white' }}
                    maxLength={5000}
                  />
                  <div style={{ fontSize: '0.7rem', color: '#999', marginTop: '6px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <FiInfo size={12} /> Be specific about style, colors, and composition for best results
                  </div>
                </div>
                
                {/* Aspect Ratio Selection */}
                <div style={{ marginBottom: '16px' }}>
                  <label style={{
                    fontFamily: 'Avenir, sans-serif',
                    fontSize: '0.8rem',
                    color: '#666',
                    display: 'block',
                    marginBottom: '8px',
                    fontWeight: 500,
                  }}>
                    Aspect Ratio
                  </label>
                  <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                    {(['1:1', '9:16', '16:9', '3:4', '4:3']).map((ratio) => (
                      <button
                        key={ratio}
                        onClick={() => setAssetAspectRatio(ratio)}
                        style={{
                          padding: '6px 14px',
                          borderRadius: '20px',
                          border: assetAspectRatio === ratio ? '2px solid #6ba6ff' : '1px solid #ddd',
                          background: assetAspectRatio === ratio ? '#f0f4ff' : 'white',
                          cursor: 'pointer',
                          fontSize: '0.75rem',
                          color: assetAspectRatio === ratio ? '#6ba6ff' : '#666',
                          fontFamily: 'Avenir, sans-serif',
                          fontWeight: assetAspectRatio === ratio ? 600 : 400,
                          transition: 'all 0.2s',
                        }}
                      >
                        {ratio}
                        {ratio === '9:16' && ' 📱'}
                        {ratio === '16:9' && ' 🖥️'}
                        {ratio === '3:4' && ' 📐'}
                      </button>
                    ))}
                  </div>
                  <div style={{ fontSize: '0.65rem', color: '#999', marginTop: '4px' }}>
                    {assetAspectRatio === '9:16' && 'Vertical portrait — ideal for 36x48 canvas'}
                    {assetAspectRatio === '16:9' && 'Horizontal widescreen'}
                    {assetAspectRatio === '3:4' && 'Vertical 3:4 — classic print ratio'}
                    {assetAspectRatio === '4:3' && 'Horizontal 4:3 — classic photography ratio'}
                    {assetAspectRatio === '1:1' && 'Square — ideal for social media'}
                  </div>
                </div>

                {/* Model Selection */}
                <div style={{ marginBottom: '16px' }}>
                  <label style={{
                    fontFamily: 'Avenir, sans-serif',
                    fontSize: '0.8rem',
                    color: '#666',
                    display: 'block',
                    marginBottom: '8px',
                    fontWeight: 500,
                  }}>
                    AI Model
                  </label>
                  <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                    {assetModels.map((model) => (
                      <button
                        key={model.id}
                        onClick={() => {
                          setSelectedAssetModel(model);
                          // Auto-select first supported aspect ratio
                          if (model.supportedAspectRatios && !model.supportedAspectRatios.includes(assetAspectRatio)) {
                            setAssetAspectRatio(model.supportedAspectRatios[0] || '1:1');
                          }
                        }}
                        style={{
                          padding: '10px 18px',
                          borderRadius: '12px',
                          border: selectedAssetModel?.id === model.id ? '2px solid #6ba6ff' : '1px solid #ddd',
                          background: selectedAssetModel?.id === model.id ? '#f0f4ff' : 'white',
                          cursor: 'pointer',
                          fontFamily: 'Avenir, sans-serif',
                          fontSize: '0.8rem',
                          color: selectedAssetModel?.id === model.id ? '#6ba6ff' : '#666',
                          fontWeight: selectedAssetModel?.id === model.id ? 600 : 400,
                          transition: 'all 0.2s',
                          flex: 1,
                          minWidth: '120px',
                          textAlign: 'center',
                        }}
                      >
                        <div style={{ fontWeight: 600 }}>{model.name}</div>
                        <div style={{ fontSize: '0.6rem', color: '#999', marginTop: '2px' }}>{model.description}</div>
                      </button>
                    ))}
                  </div>
                </div>
                
                {/* Generate Button */}
                <button
                  onClick={generateAssetArt}
                  disabled={isGeneratingAsset || !assetPrompt.trim() || assetPrompt.length > 5000}
                  className="btn-primary"
                  style={{ 
                    width: '100%', 
                    padding: '14px',
                    background: selectedAssetModel?.id === 'nanobanana' 
                      ? 'linear-gradient(135deg, #6ba6ff 0%, #4a8af4 100%)' 
                      : 'linear-gradient(135deg, #88b7ff 0%, #6ba6ff 100%)',
                  }}
                >
                  {isGeneratingAsset ? (
                    <>
                      <div style={{ width: '18px', height: '18px', border: '2px solid white', borderTop: '2px solid transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                      Generating Art...
                    </>
                  ) : (
                    <><FiImage size={16} /> Generate Canvas Art</>
                  )}
                </button>
                
                {assetGenerationStatus && (
                  <div style={{ marginTop: '12px', fontFamily: 'Avenir, sans-serif', fontSize: '0.8rem', color: '#6ba6ff', textAlign: 'center', padding: '8px', background: '#f0f4ff', borderRadius: '8px' }}>
                    {assetGenerationStatus}
                  </div>
                )}
                
                {/* Output Preview */}
                {assetImageUrl && (
                  <div style={{ marginTop: '20px' }} className="animate-slide-up">
                    <div style={{
                      borderRadius: '12px',
                      overflow: 'hidden',
                      border: '1px solid #eaeaea',
                      background: '#fafafa',
                      position: 'relative',
                    }}>
                      <img
                        src={assetImageUrl}
                        alt="Generated canvas art"
                        style={{
                          width: '100%',
                          maxHeight: '60vh',
                          objectFit: 'contain',
                          display: 'block',
                        }}
                        loading="lazy"
                      />
                      
                      {/* Download and Copy buttons overlay */}
                      <div style={{
                        position: 'absolute',
                        bottom: '16px',
                        right: '16px',
                        display: 'flex',
                        gap: '10px',
                      }}>
                        <button
                          onClick={() => {
                            navigator.clipboard.writeText(assetPrompt);
                            setAssetGenerationStatus('✓ Prompt copied!');
                            setTimeout(() => setAssetGenerationStatus(''), 2000);
                          }}
                          style={{
                            padding: '8px 16px',
                            borderRadius: '20px',
                            background: 'rgba(255,255,255,0.9)',
                            border: '1px solid #ddd',
                            cursor: 'pointer',
                            fontFamily: 'Avenir, sans-serif',
                            fontSize: '0.75rem',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            color: '#333',
                            backdropFilter: 'blur(4px)',
                          }}
                        >
                          <FiCopy size={14} /> Copy Prompt
                        </button>
                        <button
                          onClick={() => downloadAssetImage()}
                          style={{
                            padding: '8px 20px',
                            borderRadius: '20px',
                            background: 'rgba(107, 166, 255, 0.95)',
                            border: 'none',
                            cursor: 'pointer',
                            fontFamily: 'Avenir, sans-serif',
                            fontSize: '0.75rem',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            color: 'white',
                            fontWeight: 600,
                            backdropFilter: 'blur(4px)',
                          }}
                        >
                          <FiDownload size={14} /> Download PNG
                        </button>
                      </div>
                    </div>
                    
                    {/* Metadata */}
                    <div style={{
                      display: 'flex',
                      gap: '12px',
                      marginTop: '12px',
                      flexWrap: 'wrap',
                    }}>
                      <div style={{
                        padding: '4px 12px',
                        background: '#f0f4ff',
                        borderRadius: '16px',
                        fontSize: '0.7rem',
                        fontFamily: 'Avenir, sans-serif',
                        color: '#6ba6ff',
                      }}>
                        {selectedAssetModel?.name || 'AI'} • Flat Canvas
                      </div>
                      <div style={{
                        padding: '4px 12px',
                        background: '#f0f4ff',
                        borderRadius: '16px',
                        fontSize: '0.7rem',
                        fontFamily: 'Avenir, sans-serif',
                        color: '#6ba6ff',
                      }}>
                        {assetSize || '4K'} • 1:1
                      </div>
                      <div style={{
                        padding: '4px 12px',
                        background: '#f0f4ff',
                        borderRadius: '16px',
                        fontSize: '0.7rem',
                        fontFamily: 'Avenir, sans-serif',
                        color: '#6ba6ff',
                      }}>
                        {new Date().toLocaleDateString()}
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Quick Examples */}
                <div style={{ marginTop: '20px' }}>
                  <div style={{
                    fontSize: '0.7rem',
                    color: '#999',
                    fontFamily: 'Avenir, sans-serif',
                    marginBottom: '8px',
                    fontWeight: 500,
                  }}>
                    Quick Examples
                  </div>
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    {[
                      'Abstract geometric shapes in muted earth tones',
                      'Minimalist botanical line art on cream background',
                      'Watercolor splatter in warm pastels',
                      'Monochromatic architectural blueprint style',
                      'Colorful mosaic tile pattern',
                      'Zen garden sand ripple design',
                    ].map((example) => (
                      <button
                        key={example}
                        onClick={() => setAssetPrompt(example)}
                        style={{
                          padding: '4px 12px',
                          borderRadius: '16px',
                          border: '1px solid #ddd',
                          background: 'white',
                          cursor: 'pointer',
                          fontFamily: 'Avenir, sans-serif',
                          fontSize: '0.7rem',
                          color: '#666',
                          transition: 'all 0.2s',
                          whiteSpace: 'nowrap',
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.borderColor = '#6ba6ff';
                          e.currentTarget.style.color = '#6ba6ff';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.borderColor = '#ddd';
                          e.currentTarget.style.color = '#666';
                        }}
                      >
                        {example}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}
            
            {/* STEP 2: SCENE CREATION */}
            {currentStep === 'scene' && generatedTexture && (
              <div className="workflow-card animate-slide-up">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                  <h3 style={{
                    fontFamily: 'Avenir, sans-serif',
                    fontWeight: 600,
                    fontSize: '1.1rem',
                    color: '#333',
                    margin: 0,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                  }}>
                    <span style={{
                      background: '#88b7ff',
                      color: 'white',
                      width: '24px',
                      height: '24px',
                      borderRadius: '50%',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '0.8rem',
                    }}>2</span>
                    Create Scene
                  </h3>
                  
                  <button
                    onClick={() => setCurrentStep('texture')}
                    className="btn-secondary"
                    style={{ padding: '6px 12px', fontSize: '0.75rem' }}
                  >
                    ← Back to Texture
                  </button>
                </div>
                
                <div style={{
                  padding: '14px 18px',
                  background: 'linear-gradient(135deg, #f0f4ff 0%, #e8f0ff 100%)',
                  borderRadius: '12px',
                  border: '1px solid #c5d0f0',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '14px',
                  marginBottom: '20px',
                  flexWrap: 'wrap',
                }}>
                  <div style={{
                    width: '50px',
                    height: '50px',
                    borderRadius: '8px',
                    overflow: 'hidden',
                    border: '2px solid #88b7ff',
                    flexShrink: 0,
                  }}>
                    <img src={generatedTexture.imageUrl} alt="Texture" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '0.7rem', color: '#88b7ff', textTransform: 'uppercase', letterSpacing: '1px', fontFamily: 'Avenir, sans-serif', fontWeight: 600 }}>
                      Applied Texture
                    </div>
                    <div style={{ fontFamily: 'Avenir, sans-serif', fontWeight: 600, color: '#333', fontSize: '0.9rem' }}>
                      {textureShortPrompt}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      onClick={() => setIsTextureSelectionMode(true)}
                      className="btn-secondary"
                      style={{ padding: '6px 12px', fontSize: '0.7rem' }}
                    >
                      Change
                    </button>
                  </div>
                </div>
                
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '20px' }}>
                  <div>
                    <label style={{
                      fontFamily: 'Avenir, sans-serif',
                      fontSize: '0.8rem',
                      color: '#666',
                      display: 'block',
                      marginBottom: '8px',
                      fontWeight: 500,
                    }}>
                      Aspect Ratio
                    </label>
                    <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                      {getAspectRatios().map((ratio) => (
                        <button
                          key={ratio}
                          onClick={() => setAspectRatio(ratio)}
                          style={{
                            padding: '6px 14px',
                            borderRadius: '20px',
                            border: aspectRatio === ratio ? '2px solid #88b7ff' : '1px solid #ddd',
                            background: aspectRatio === ratio ? '#f0f4ff' : 'white',
                            cursor: 'pointer',
                            fontSize: '0.75rem',
                            color: aspectRatio === ratio ? '#88b7ff' : '#666',
                            fontFamily: 'Avenir, sans-serif',
                            fontWeight: aspectRatio === ratio ? 600 : 400,
                          }}
                        >
                          {ratio}
                        </button>
                      ))}
                    </div>
                  </div>
                  
                  <div>
                    <label style={{
                      fontFamily: 'Avenir, sans-serif',
                      fontSize: '0.8rem',
                      color: '#666',
                      display: 'block',
                      marginBottom: '8px',
                      fontWeight: 500,
                    }}>
                      Resolution
                    </label>
                    <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                      {getSizes().map((size) => (
                        <button
                          key={size}
                          onClick={() => setImageSize(size)}
                          style={{
                            padding: '6px 14px',
                            borderRadius: '20px',
                            border: imageSize === size ? '2px solid #88b7ff' : '1px solid #ddd',
                            background: imageSize === size ? '#f0f4ff' : 'white',
                            cursor: 'pointer',
                            fontSize: '0.75rem',
                            color: imageSize === size ? '#88b7ff' : '#666',
                            fontFamily: 'Avenir, sans-serif',
                            fontWeight: imageSize === size ? 600 : 400,
                          }}
                        >
                          {size}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Upscale toggle – only for non‑Seedream models */}
                  {selectedSceneModel.id !== 'seedream' && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', paddingTop: '10px' }}>
                      <label style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        cursor: 'pointer',
                        fontFamily: 'Avenir, sans-serif',
                        fontSize: '0.8rem',
                        color: '#333',
                        fontWeight: 500,
                      }}>
                        <input
                          type="checkbox"
                          checked={upscaleEnabled}
                          onChange={(e) => setUpscaleEnabled(e.target.checked)}
                          style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                        />
                        Upscale 2x (HD+)
                      </label>
                      <span style={{ fontSize: '0.7rem', color: '#88b7ff' }}>(1 credit)</span>
                    </div>
                  )}
                </div>
                
                <div style={{ marginBottom: '16px' }}>
  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
    <label style={{
      fontFamily: 'Avenir, sans-serif',
      fontSize: '0.8rem',
      color: '#666',
      fontWeight: 500,
    }}>
      Describe Your Scene
      <span style={{ fontSize: '0.7rem', color: '#999', marginLeft: '8px', fontWeight: 400 }}>
        — Use [TEXTURE] badge to place your texture
      </span>
    </label>
    <SceneCharCount count={sceneShortPrompt.length} />
  </div>

  <div style={{ marginBottom: '16px' }}>
    <label style={{
      fontFamily: 'Avenir, sans-serif',
      fontSize: '0.8rem',
      color: '#666',
      display: 'block',
      marginBottom: '8px',
      fontWeight: 500,
    }}>
      Camera Angle
    </label>
    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
      {cameraAngles.map(angle => (
        <button
          key={angle.id}
          onClick={() => setSelectedCameraAngle(angle.id)}
          style={{
            padding: '6px 14px',
            borderRadius: '20px',
            border: selectedCameraAngle === angle.id ? '2px solid #88b7ff' : '1px solid #ddd',
            background: selectedCameraAngle === angle.id ? '#f0f4ff' : 'white',
            cursor: 'pointer',
            fontSize: '0.7rem',
            color: selectedCameraAngle === angle.id ? '#88b7ff' : '#666',
            fontFamily: 'Avenir, sans-serif',
            fontWeight: selectedCameraAngle === angle.id ? 600 : 400,
            transition: 'all 0.2s',
          }}
          title={angle.description}
        >
          {angle.label}
        </button>
      ))}
    </div>
  </div>

  <div style={{ marginBottom: '16px' }}>
    <label style={{
      fontFamily: 'Avenir, sans-serif',
      fontSize: '0.8rem',
      color: '#666',
      display: 'block',
      marginBottom: '8px',
      fontWeight: 500,
    }}>
      Optional Architectural & Decor Details
    </label>
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
      <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontFamily: 'Avenir, sans-serif', fontSize: '0.75rem', color: includeArt ? '#88b7ff' : '#666' }}>
        <input type="checkbox" checked={includeArt} onChange={(e) => setIncludeArt(e.target.checked)} style={{ width: '16px', height: '16px', cursor: 'pointer' }} />
        Wall Art
      </label>
      <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontFamily: 'Avenir, sans-serif', fontSize: '0.75rem', color: includePaintedWalls ? '#88b7ff' : '#666' }}>
        <input type="checkbox" checked={includePaintedWalls} onChange={(e) => setIncludePaintedWalls(e.target.checked)} style={{ width: '16px', height: '16px', cursor: 'pointer' }} />
        Painted Walls
      </label>
      <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontFamily: 'Avenir, sans-serif', fontSize: '0.75rem', color: includeCeiling ? '#88b7ff' : '#666' }}>
        <input type="checkbox" checked={includeCeiling} onChange={(e) => setIncludeCeiling(e.target.checked)} style={{ width: '16px', height: '16px', cursor: 'pointer' }} />
        Ceiling Type
      </label>
      <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontFamily: 'Avenir, sans-serif', fontSize: '0.75rem', color: includeMoulding ? '#88b7ff' : '#666' }}>
        <input type="checkbox" checked={includeMoulding} onChange={(e) => setIncludeMoulding(e.target.checked)} style={{ width: '16px', height: '16px', cursor: 'pointer' }} />
        Moulding
      </label>
      <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontFamily: 'Avenir, sans-serif', fontSize: '0.75rem', color: includeLighting ? '#88b7ff' : '#666' }}>
        <input type="checkbox" checked={includeLighting} onChange={(e) => setIncludeLighting(e.target.checked)} style={{ width: '16px', height: '16px', cursor: 'pointer' }} />
        Lighting
      </label>
      <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontFamily: 'Avenir, sans-serif', fontSize: '0.75rem', color: includeDoor ? '#88b7ff' : '#666' }}>
        <input type="checkbox" checked={includeDoor} onChange={(e) => setIncludeDoor(e.target.checked)} style={{ width: '16px', height: '16px', cursor: 'pointer' }} />
        Door
      </label>
      <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontFamily: 'Avenir, sans-serif', fontSize: '0.75rem', color: includeDecor ? '#88b7ff' : '#666' }}>
        <input type="checkbox" checked={includeDecor} onChange={(e) => setIncludeDecor(e.target.checked)} style={{ width: '16px', height: '16px', cursor: 'pointer' }} />
        Decor Object
      </label>
      <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontFamily: 'Avenir, sans-serif', fontSize: '0.75rem', color: includeTechnique ? '#88b7ff' : '#666' }}>
        <input type="checkbox" checked={includeTechnique} onChange={(e) => setIncludeTechnique(e.target.checked)} style={{ width: '16px', height: '16px', cursor: 'pointer' }} />
        Wall Technique
      </label>
      <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontFamily: 'Avenir, sans-serif', fontSize: '0.75rem', color: includeHardware ? '#88b7ff' : '#666' }}>
        <input type="checkbox" checked={includeHardware} onChange={(e) => setIncludeHardware(e.target.checked)} style={{ width: '16px', height: '16px', cursor: 'pointer' }} />
        Hardware
      </label>
      <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontFamily: 'Avenir, sans-serif', fontSize: '0.75rem', color: includeRug ? '#88b7ff' : '#666' }}>
        <input type="checkbox" checked={includeRug} onChange={(e) => setIncludeRug(e.target.checked)} style={{ width: '16px', height: '16px', cursor: 'pointer' }} />
        Rug
      </label>
      <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontFamily: 'Avenir, sans-serif', fontSize: '0.75rem', color: includeFlooring ? '#88b7ff' : '#666' }}>
        <input type="checkbox" checked={includeFlooring} onChange={(e) => setIncludeFlooring(e.target.checked)} style={{ width: '16px', height: '16px', cursor: 'pointer' }} />
        Flooring
      </label>
    </div>
  </div>

  
  <div style={{ position: 'relative' }}>
    <textarea
      ref={sceneInputRef}
      value={sceneShortPrompt}
      onChange={(e) => setSceneShortPrompt(e.target.value)}
      rows={4}
      className="input-field"
      style={{ paddingBottom: '40px', background: 'white' }}
      placeholder='Describe your scene... (e.g., "A cozy living room with a coffee table, velvet sofa, and large windows")'
      autoComplete="off"
      spellCheck={false}
      maxLength={750}
    />
    
    {generatedTexture && (
      <button
        onClick={insertTexturePlaceholderAtCursor}
        style={{
          position: 'absolute',
          bottom: '10px',
          right: '10px',
          background: 'rgba(136, 183, 255, 0.1)',
          border: '1px solid rgba(136, 183, 255, 0.2)',
          padding: '4px 12px',
          borderRadius: '12px',
          cursor: 'pointer',
          fontSize: '0.7rem',
          color: '#88b7ff',
          fontFamily: 'Avenir, sans-serif',
        }}
      >
        <FiPlus size={12} style={{ marginRight: '4px' }} />
        Insert Texture
      </button>
    )}
  </div>
</div>
                
                
                {/* Global Kling Realism Toggle */}
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '10px', 
                  marginBottom: '16px',
                  padding: '10px 16px',
                  background: enableGlobalRealism ? '#f0f4ff' : '#f9f9f9',
                  borderRadius: '12px',
                  border: enableGlobalRealism ? '1px solid #88b7ff' : '1px solid #e0e0e0',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                }} onClick={() => setEnableGlobalRealism(!enableGlobalRealism)}>
                  <input
                    type="checkbox"
                    checked={enableGlobalRealism}
                    onChange={(e) => setEnableGlobalRealism(e.target.checked)}
                    style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                  />
                  <div>
                    <div style={{ fontFamily: 'Avenir, sans-serif', fontWeight: 600, fontSize: '0.8rem', color: enableGlobalRealism ? '#88b7ff' : '#333' }}>
                      Apply Kling‑style realism to all models
                    </div>
                    <div style={{ fontFamily: 'Avenir, sans-serif', fontSize: '0.7rem', color: '#888' }}>
                      (Positive prompt + grain injection)
                    </div>
                  </div>
                </div>

                <button
                  onClick={generateRandomScene}
                  disabled={isExpandingScene}
                  className="btn-secondary"
                  style={{ width: '100%', marginBottom: '8px', justifyContent: 'center' }}
                >
                  <FiRefreshCcw size={16} /> Randomize Scene
                </button>
                <button
                  onClick={expandScenePrompt}
                  disabled={isExpandingScene || !sceneShortPrompt.trim()}
                  className="btn-secondary"
                  style={{ width: '100%', marginBottom: '16px', justifyContent: 'center' }}
                >
                  {isExpandingScene ? (
                    <>
                      <div style={{ width: '16px', height: '16px', border: '2px solid #88b7ff', borderTop: '2px solid transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                      Composing Scene...
                    </>
                  ) : (
                    <><FiZap size={16} /> Expand Scene with AI</>
                  )}
                </button>
                
                {sceneExpandedPrompt && (
                  <div className="animate-slide-up">
                    <div style={{ display: 'flex', gap: '4px', marginBottom: '8px', background: '#f0f0f0', borderRadius: '8px', padding: '4px', width: 'fit-content' }}>
                      <button
                        onClick={() => setActiveGuidelineTab('prompt')}
                        style={{
                          padding: '6px 14px',
                          borderRadius: '6px',
                          border: 'none',
                          background: activeGuidelineTab === 'prompt' ? 'white' : 'transparent',
                          color: activeGuidelineTab === 'prompt' ? '#333' : '#666',
                          cursor: 'pointer',
                          fontFamily: 'Avenir, sans-serif',
                          fontSize: '0.75rem',
                          fontWeight: activeGuidelineTab === 'prompt' ? 600 : 400,
                        }}
                      >
                        Full Prompt
                      </button>
                      <button
                        onClick={() => setActiveGuidelineTab('guidelines')}
                        style={{
                          padding: '6px 14px',
                          borderRadius: '6px',
                          border: 'none',
                          background: activeGuidelineTab === 'guidelines' ? 'white' : 'transparent',
                          color: activeGuidelineTab === 'guidelines' ? '#333' : '#666',
                          cursor: 'pointer',
                          fontFamily: 'Avenir, sans-serif',
                          fontSize: '0.75rem',
                          fontWeight: activeGuidelineTab === 'guidelines' ? 600 : 400,
                        }}
                      >
                        Scene Guidelines
                      </button>
                    </div>
                    
                    {activeGuidelineTab === 'prompt' ? (
                      <div>
                        <textarea
                          value={sceneExpandedPrompt}
                          onChange={(e) => setSceneExpandedPrompt(e.target.value)}
                          rows={8}
                          className="input-field"
                          style={{ fontFamily: 'Roboto Mono, sans-serif', fontSize: '0.7rem', background: 'white' }}
                        />
                        <div style={{ fontSize: '0.7rem', color: sceneExpandedPrompt.length > 2300 ? '#e02f1d' : '#999', marginTop: '4px' }}>
                          {sceneExpandedPrompt.length}/2300 characters {sceneExpandedPrompt.length > 2300 && '⚠️ Approaching Kling 2500 limit'}
                        </div>
                      </div>
                    ) : (
                      renderGuidelinesPanel(sceneGuidelines, 'scene')
                    )}
                  </div>
                )}
                
                <div style={{ marginBottom: '16px', position: 'relative', marginTop: '16px' }}>
                  <label style={{
                    fontFamily: 'Avenir, sans-serif',
                    fontSize: '0.8rem',
                    color: '#666',
                    display: 'block',
                    marginBottom: '8px',
                    fontWeight: 500,
                  }}>
                    AI Model
                  </label>
                  <button
                    onClick={() => setShowModelDropdown(!showModelDropdown)}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      background: 'white',
                      border: '1px solid #ddd',
                      borderRadius: '12px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      cursor: 'pointer',
                      fontFamily: 'Avenir, sans-serif',
                      fontSize: '0.85rem',
                      color: '#333',
                    }}
                  >
                    <span>{selectedSceneModel.name}</span>
                    <FiChevronDown size={16} style={{ transform: showModelDropdown ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
                  </button>
                  
                  {showModelDropdown && (
                    <div className="model-dropdown" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                      {models.map((model) => (
                        <div
                          key={model.id}
                          className={`model-option ${selectedSceneModel.id === model.id ? 'selected' : ''}`}
                          onClick={() => { setSelectedSceneModel(model); setShowModelDropdown(false); }}
                        >
                          <div style={{ fontWeight: selectedSceneModel.id === model.id ? 600 : 400 }}>{model.name}</div>
                          <div style={{ fontSize: '0.7rem', color: '#888', marginTop: '2px' }}>{model.description}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                
                <button
                  onClick={generateFinalScene}
                  disabled={isGeneratingScene || isUpscaling || !sceneExpandedPrompt.trim() || sceneExpandedPrompt.length > 2500}
                  className="btn-primary"
                  style={{ width: '100%', padding: '14px' }}
                >
                  {isUpscaling ? (
                    <>
                      <div style={{ width: '18px', height: '18px', border: '2px solid white', borderTop: '2px solid transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                      Upscaling to 4K…
                    </>
                  ) : isGeneratingScene ? (
                    <>
                      <div style={{ width: '18px', height: '18px', border: '2px solid white', borderTop: '2px solid transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                      Generating Scene...
                    </>
                  ) : (
                    <><FiCamera size={16} /> Generate Smartphone Photo</>
                  )}
                </button>
                
                {generationStatus && (
                  <div style={{ marginTop: '12px', fontFamily: 'Avenir, sans-serif', fontSize: '0.8rem', color: '#88b7ff', textAlign: 'center', padding: '8px', background: '#f0f4ff', borderRadius: '8px' }}>
                    {generationStatus}
                  </div>
                )}
              </div>
            )}
            
            {/* STEP 3: RESULT */}
            {currentStep === 'result' && generatedScene && (
              <div className="workflow-card animate-slide-up">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                  <h3 style={{
                    fontFamily: 'Avenir, sans-serif',
                    fontWeight: 600,
                    fontSize: '1.1rem',
                    color: '#333',
                    margin: 0,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                  }}>
                    <span style={{
                      background: '#4caf50',
                      color: 'white',
                      width: '24px',
                      height: '24px',
                      borderRadius: '50%',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '0.8rem',
                    }}>✓</span>
                    Scene Complete!
                  </h3>
                  
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      onClick={() => setCurrentStep('scene')}
                      className="btn-secondary"
                      style={{ padding: '6px 14px', fontSize: '0.75rem', background: '#88b7ff', color: 'white', border: 'none' }}
                    >
                      <FiRefreshCcw size={14} /> Regenerate
                    </button>
                    <button
                      onClick={resetWorkflow}
                      className="btn-secondary"
                      style={{ padding: '6px 14px', fontSize: '0.75rem' }}
                    >
                      New Project
                    </button>
                  </div>
                </div>
                
                <div style={{
                  borderRadius: '12px',
                  overflow: 'hidden',
                  border: '1px solid #eaeaea',
                  marginBottom: '20px',
                  background: '#1a1a1a',
                }}>
                  <img
                    src={generatedScene.imageUrl}
                    alt="Generated scene"
                    style={{
                      width: '100%',
                      maxHeight: '60vh',
                      objectFit: 'contain',
                      display: 'block',
                    }}
                    loading="lazy"
                  />
                </div>
                
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '16px', marginBottom: '20px' }}>
                  <div style={{ padding: '16px', background: '#f9f9f9', borderRadius: '12px' }}>
                    <div style={{ fontSize: '0.7rem', color: '#88b7ff', textTransform: 'uppercase', letterSpacing: '1px', fontFamily: 'Avenir, sans-serif', fontWeight: 600, marginBottom: '8px' }}>
                      Scene Description
                    </div>
                    <div style={{ fontFamily: 'Avenir, sans-serif', fontSize: '0.85rem', color: '#333', lineHeight: '1.5' }}>
                      {generatedScene.shortSceneDescription || sceneShortPrompt}
                    </div>
                  </div>
                  
                  <div style={{ padding: '16px', background: '#f9f9f9', borderRadius: '12px' }}>
                    <div style={{ fontSize: '0.7rem', color: '#88b7ff', textTransform: 'uppercase', letterSpacing: '1px', fontFamily: 'Avenir, sans-serif', fontWeight: 600, marginBottom: '8px' }}>
                      Applied Texture
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <div style={{ width: '40px', height: '40px', borderRadius: '8px', overflow: 'hidden', border: '1px solid #ddd' }}>
                        <img src={generatedScene.textureImageUrl || generatedTexture?.imageUrl} alt="Texture" style={{ width: '100%', height: '100%', objectFit: 'cover' }} loading="lazy" />
                      </div>
                      <div style={{ fontFamily: 'Avenir, sans-serif', fontSize: '0.85rem', color: '#333' }}>
                        {generatedScene.textureShortDescription || textureShortPrompt}
                      </div>
                    </div>
                  </div>
                  
                  <div style={{ padding: '16px', background: '#f9f9f9', borderRadius: '12px' }}>
                    <div style={{ fontSize: '0.7rem', color: '#88b7ff', textTransform: 'uppercase', letterSpacing: '1px', fontFamily: 'Avenir, sans-serif', fontWeight: 600, marginBottom: '8px' }}>
                      Technical Details
                    </div>
                    <div style={{ fontFamily: 'Avenir, sans-serif', fontSize: '0.8rem', color: '#666', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      <div>Model: {generatedScene.model}</div>
                      <div>Resolution: {generatedScene.resolution}</div>
                      <div>Aspect Ratio: {generatedScene.aspectRatio}</div>
                    </div>
                  </div>
                </div>
                
                <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                  <button
                    onClick={() => downloadImage(generatedScene.imageUrl, generatedScene.id)}
                    className="btn-primary"
                    style={{ flex: 1, minWidth: '120px' }}
                  >
                    <FiDownload size={16} /> Download
                  </button>
                  <button
                    onClick={() => copyToClipboard(generatedScene.prompt)}
                    className="btn-secondary"
                    style={{ flex: 1, minWidth: '120px', justifyContent: 'center' }}
                  >
                    <FiCopy size={16} /> Copy Prompt
                  </button>
                  <button
                    onClick={() => upscaleImage(generatedScene)}
                    disabled={isUpscaling}
                    className="btn-secondary"
                    style={{ flex: 1, minWidth: '120px', justifyContent: 'center', background: isUpscaling ? '#f5f5f5' : 'transparent' }}
                  >
                    {isUpscaling ? (
                      <>
                        <div style={{ width: '16px', height: '16px', border: '2px solid #88b7ff', borderTop: '2px solid transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                        Upscaling…
                      </>
                    ) : (
                      <><FiZap size={16} /> Upscale (2x)</>
                    )}
                  </button>
                </div>
              </div>
            )}
            
            {/* GALLERY SECTION */}
            <div className="workflow-card">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '12px' }}>
                <h3 style={{
                  fontFamily: 'Avenir, sans-serif',
                  fontWeight: 600,
                  fontSize: '1rem',
                  color: '#333',
                  margin: 0,
                }}>
                  Gallery
                </h3>
                
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                  {/* Bulk Action Buttons */}
                  {showBulkSelect && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <button
                        onClick={selectAllImages}
                        style={{
                          padding: '6px 14px',
                          borderRadius: '18px',
                          border: '1px solid #88b7ff',
                          background: selectedImageIds.size === filteredImages.length && filteredImages.length > 0 ? '#88b7ff' : 'white',
                          color: selectedImageIds.size === filteredImages.length && filteredImages.length > 0 ? 'white' : '#88b7ff',
                          cursor: 'pointer',
                          fontFamily: 'Avenir, sans-serif',
                          fontSize: '0.75rem',
                          fontWeight: 500,
                          transition: 'all 0.2s',
                        }}
                      >
                        {selectedImageIds.size === filteredImages.length && filteredImages.length > 0 ? 'Deselect All' : 'Select All'}
                      </button>
                      <button
                        onClick={bulkDownload}
                        disabled={selectedImageIds.size === 0 || isBulkDownloading}
                        style={{
                          padding: '6px 18px',
                          borderRadius: '18px',
                          border: 'none',
                          background: selectedImageIds.size > 0 && !isBulkDownloading ? '#4caf50' : '#ccc',
                          color: 'white',
                          cursor: selectedImageIds.size > 0 && !isBulkDownloading ? 'pointer' : 'not-allowed',
                          fontFamily: 'Avenir, sans-serif',
                          fontSize: '0.75rem',
                          fontWeight: 500,
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          transition: 'all 0.2s',
                        }}
                      >
                        {isBulkDownloading ? (
                          <>
                            <div style={{ width: '14px', height: '14px', border: '2px solid white', borderTop: '2px solid transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                            Zipping...
                          </>
                        ) : (
                          <>
                            <FiDownload size={14} />
                            Download {selectedImageIds.size > 0 ? `(${selectedImageIds.size})` : ''}
                          </>
                        )}
                      </button>
                      <button
                        onClick={() => {
                          setShowBulkSelect(false);
                          setSelectedImageIds(new Set());
                        }}
                        style={{
                          padding: '6px 12px',
                          borderRadius: '18px',
                          border: '1px solid #ddd',
                          background: 'transparent',
                          color: '#999',
                          cursor: 'pointer',
                          fontFamily: 'Avenir, sans-serif',
                          fontSize: '0.75rem',
                        }}
                      >
                        <FiX size={14} />
                      </button>
                    </div>
                  )}
                  
                  <button
                    onClick={() => {
                      setShowBulkSelect(!showBulkSelect);
                      if (showBulkSelect) setSelectedImageIds(new Set());
                    }}
                    style={{
                      padding: '6px 14px',
                      borderRadius: '18px',
                      border: showBulkSelect ? '2px solid #88b7ff' : '1px solid #ddd',
                      background: showBulkSelect ? '#e8f0ff' : 'transparent',
                      color: showBulkSelect ? '#88b7ff' : '#666',
                      cursor: 'pointer',
                      fontFamily: 'Avenir, sans-serif',
                      fontSize: '0.75rem',
                      fontWeight: showBulkSelect ? 600 : 400,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      transition: 'all 0.2s',
                    }}
                  >
                    <FiGrid size={14} />
                    {showBulkSelect ? 'Cancel' : 'Select'}
                  </button>
                  
                  <div className="filter-bar">
                    <button
                      className={`filter-chip ${galleryFilter === 'all' ? 'active' : ''}`}
                      onClick={() => setGalleryFilter('all')}
                    >
                      All ({generatedImages.length})
                    </button>
                    <button
                      className={`filter-chip ${galleryFilter === 'textures' ? 'active' : ''}`}
                      onClick={() => setGalleryFilter('textures')}
                    >
                      Textures ({generatedImages.filter(img => img.type === 'texture').length})
                    </button>
                    <button
                      className={`filter-chip ${galleryFilter === 'scenes' ? 'active' : ''}`}
                      onClick={() => setGalleryFilter('scenes')}
                    >
                      Scenes ({generatedImages.filter(img => img.type === 'scene' || img.workflowType === 'texture-to-scene').length})
                    </button>
                  </div>
                  
                  {/* Sort by creation date */}
                  <select
                    value={sortOrder}
                    onChange={(e) => setSortOrder(e.target.value)}
                    style={{
                      padding: '6px 12px',
                      borderRadius: '18px',
                      border: '1px solid #ddd',
                      fontFamily: 'Avenir, sans-serif',
                      fontSize: '0.75rem',
                      color: '#666',
                      cursor: 'pointer',
                      background: 'white',
                    }}
                  >
                    <option value="desc">Newest first</option>
                    <option value="asc">Oldest first</option>
                  </select>
                </div>
              </div>


              
              {(() => {
                // Determine column count based on screen width
                const columnCount = isMobile ? 2 : (typeof window !== 'undefined' && window.innerWidth <= 1024 ? 3 : 4);
                
                // Only show the first `displayCount` images
                const imagesToShow = filteredImages.slice(0, displayCount);
                
                // Distribute items row‑first into columns
                const columns = Array.from({ length: columnCount }, () => []);
                imagesToShow.forEach((image, index) => {
                  const colIndex = index % columnCount;
                  columns[colIndex].push(image);
                });
                
                if (filteredImages.length === 0) {
                  return (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '60px 20px', textAlign: 'center' }}>
                      <FiImage size={48} color="#ccc" />
                      <p style={{ fontFamily: 'Avenir, sans-serif', fontSize: '1rem', color: '#999', marginTop: '16px' }}>
                        {galleryFilter === 'textures' ? 'No textures yet' : galleryFilter === 'scenes' ? 'No scenes yet' : 'No images yet'}
                      </p>
                      <p style={{ fontFamily: 'Avenir, sans-serif', fontSize: '0.8rem', color: '#bbb' }}>
                        Start by generating a texture in Step 1
                      </p>
                    </div>
                  );
                }
                
                return (
                  <>
                    <div style={{
                      display: 'flex',
                      gap: '8px',
                      alignItems: 'flex-start',
                      width: '100%',
                    }}>
                      {columns.map((colImages, colIdx) => (
                        <div key={colIdx} style={{
                          flex: 1,
                          minWidth: 0,
                          overflow: 'hidden',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '8px',
                        }}>
                          {colImages.map((image) => (
                            <GalleryItem
                              key={image.id}
                              image={image}
                              onSelect={() => handleSelectImage(image)}
                              onDownload={() => handleDownloadImage(image.imageUrl, image.id)}
                              onDelete={() => handleDeleteImage(image)}
                              onReuse={() => handleReuseImage(image)}
                              onToggleSelect={toggleSelectImage}
                              isSelected={selectedImageIds.has(image.id)}
                              showCheckboxes={showBulkSelect}
                              onUpscale={handleUpscaleImage}
                            />
                          ))}
                        </div>
                      ))}
                    </div>
                    {displayCount < filteredImages.length && (
                      <div style={{ textAlign: 'center', marginTop: '16px' }}>
                        <button
                          onClick={() => setDisplayCount(prev => prev + 12)}
                          className="btn-secondary"
                          style={{ padding: '10px 32px' }}
                        >
                          Show More ({filteredImages.length - displayCount} remaining)
                        </button>
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          </div>
        </div>
      </div>
      
      {/* IMAGE DETAILS MODAL */}
      {showImageDetails && selectedImage && (
        <ImageDetailsModal
          selectedImage={selectedImage}
          onClose={() => { setShowImageDetails(false); setSelectedImage(null); }}
          onDownload={() => downloadImage(selectedImage.imageUrl, selectedImage.id)}
          onDelete={() => deleteImage(selectedImage, false)}
          onUseTexture={() => useTextureFromGallery(selectedImage)}
          isMobile={isMobile}
        />
      )}
      
            {/* TEXTURE SELECTION MODAL */}
      {isTextureSelectionMode && (
        <TextureSelectionModal
          textures={generatedImages.filter(img => img.type === 'texture')}
          onSelect={useTextureFromGallery}
          onClose={() => setIsTextureSelectionMode(false)}
          isMobile={isMobile}
        />
      )}
      

    </>
  );
};

export default DevStudio;